import { ChangeDetectorRef, ComponentFactoryResolver, ComponentRef, Injectable, Type } from '@angular/core';
import { DeferredPromise } from '@cf-shared/shared-util';
import { getAllDeepLinks, getPageClass, getPageDefaultHistory, getPageFullPath, getPageHead, getPageName, getPageNameByPath, getPageSegment, getPageClassBySegment } from 'app/app-page-helpers';
import { PageRef, CallablePageRef } from './page-ref';
import { CsNavComponent } from 'components/cs-nav/cs-nav';
import { Location } from '@angular/common';
import { NavSegment, UrlService } from 'services/url.service';
import { OverlayStackService } from './overlay-stack.service';
import { NavigationBreadcrumbService, PageStackEntry } from '../services/navigation-breadcrumb.service';
import { PubSubService, navigationStackChangedEvent } from 'services/pubsub.service';
import { navStackToPages, navStackToBreadcrumb, pageRootUrl, stackHistoryUrls, NavStackLogger } from '../utils/nav-stack-converter';
import { pagesToUrl, PageWithParams } from '../utils/url-parser';
import { getWarmResumeUid } from 'services/warm-resume-storage';
import { shouldRestorePersistedNavUrl } from '../utils/nav-url-restore-policy';
import { shouldContinueNavRestore, shouldDeferReroute } from './nav-restore-coordination';
import { PostLoginRouteClaim, PostLoginRouteCoordinator } from './post-login-route-coordination';
import { runPostBrowserNavCallbacks } from './post-browser-nav-callbacks';
import { NavCallRegistry, shouldDeferCallDelivery } from './nav-call-registry';
import {
  BrowserEnvironment,
  BrowserHistory,
  HistoryEntry,
  NavigationState,
  OpenedPageHistoryOutcome,
  TimerHandle,
  clearPersistedUrl,
  getPersistedUrl,
  realBrowserEnvironment,
} from './browser-history';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { getMyApp } from 'app/app-interface';
import { safeUrlForLog } from 'app/safe-url-for-log';
import { createOncePerConditionReporter } from 'util/once-per-condition-reporter';
import {
  ephemeralLocalParams,
  generateLocalParamId,
  getStorageKey,
  hydrateLocalParams,
  openLocalParamIdsOfStack,
  processParamsForNavigation,
  retrieveLocalParam,
  LocalParamDamage,
  configureNavLocalParams,
} from '../utils/nav-local-params';

// ============================================================================
// Type-Safe Navigation Parameter Types
// ============================================================================

/**
 * Helper type to extract the 'local' property type from a params type.
 */
export type LocalParamsOf<P> = P extends { local?: infer L } ? L : never;

/**
 * Allowed types for URL-serializable navigation params.
 * Only primitives that can be safely converted to/from URL strings.
 */
type UrlSerializable = string | number | boolean | undefined;

/**
 * Type utility that enforces URL-safe navigation params.
 * Only allows primitives (string, number, boolean) and the special 'local' object.
 *
 * This allows:
 * - string, number, boolean (and optional versions) - serialized to URL
 * - 'local' object - stored in sessionStorage, not in URL
 *
 * This rejects:
 * - Functions (use callParentAsync pattern instead)
 * - Objects (use local: { ... } instead)
 * - Arrays (use local: { ... } instead)
 * - unknown, any (must be explicit about the type)
 * - 'returnContext' at root level (must be inside local: { ... })
 */
export type ValidNavParams<T> = {
  [K in keyof T]: K extends 'local'
    ? T[K]  // 'local' is allowed to be any object
    : K extends 'returnContext'
      ? never  // returnContext must be inside local: { ... }
      : unknown extends T[K]
        ? never  // reject 'unknown' type - must be explicit
        : T[K] extends UrlSerializable
          ? T[K]  // primitives are allowed
          : never  // reject objects, functions, arrays, etc.
};

// The browser-history state shape lives beside the class that writes it, and is re-exported
// here because this is where it has always been imported from.
export type { NavigationState };

/**
 * Return type for ionViewCanLeave.
 * - false: block navigation
 * - true: allow navigation
 * - { result: T }: allow navigation and pass result to returnCallback
 */
export type CanLeaveResult<T = void> = boolean | { result: T };

export interface CsPageComponentInterface<ResultType = void> {
  ionViewCanEnter(): Promise<any | void>;
  ionViewCanLeave(): Promise<CanLeaveResult<ResultType>>;
  ionViewDidEnter(): Promise<void>;
  ionViewWillEnter(): Promise<void>;
  ionViewWillLeave(): Promise<void>;
  ionViewDidLeave(): Promise<void>;
  ionViewDidReEnter(): Promise<void>;
  navParams: NavParams<any>;
  /** @internal Called by cs-nav before ionViewWillEnter */
  _navPrepareEnter(): void;
  /** @internal Called by cs-nav before ionViewWillLeave */
  _navPrepareLeave(): void;
}

/**
 * A page's "do not ask me about unsaved work THIS time" switch.
 *
 * Some pages hold unsaved edits and ask about them in `ionViewCanLeave`. A few ways out of
 * such a page have already decided what happens to those edits — an Accept button that has
 * just saved, a Cancel button, a Delete — so the page arms this switch before it pops, and
 * its guard lets that one departure through without asking.
 *
 * **Reading it always clears it, and that is the whole point of this class.** The
 * navigation layer ignores a pop while a browser-initiated one is already running, so a
 * page can outlive the pop the switch was armed for. A switch left standing turns the
 * guard off for the rest of that page's life, and the page silently stops asking about
 * unsaved work — the exact bug the guard exists to prevent. That was a real finding from
 * #5056's review. Every guarded page used to write the arm-and-clear by hand and one
 * author forgetting the clear was invisible, so the two steps live here as one call
 * instead. See `design-docs/angular-components.md`, "Cancel must never ask".
 *
 * Pages get one of these from `CsPage` as `this.leaveWithoutAsking` — they do not build
 * their own.
 */
export class OneShotLeaveFlag {
  private armed = false;

  /** Let the NEXT departure through without asking. Arming twice is the same as once. */
  set() {
    this.armed = true;
  }

  /**
   * Whether this departure was already decided — and clears the switch as it answers, so
   * the exemption covers one departure and no more.
   */
  consume(): boolean {
    const wasArmed = this.armed;
    this.armed = false;
    return wasArmed;
  }
}

/**
 * How a child's result reaches its parent, and WHEN.
 *
 * A page that opens another passes the NAME of one of its own methods in the child's params.
 * There are two names, and the parent picks which it passes:
 *
 * - `returnCallback` — runs AFTER the closing transition. The safe default; may navigate.
 * - `returnCallbackImmediate` — runs BEFORE it, as soon as the child's leave guard agrees.
 *   For DATA only: it must not navigate. It need not paint either: cs-nav redraws a page as
 *   it re-enters (#5686), so the data it writes shows up when the parent is back.
 *
 * Why the parent chooses rather than the caller, why the ordinary slot is deferred, how to
 * decide between them, and what happens when a pop is refused after the early handler ran:
 * `design-docs/navigation.md`, "Two timings".
 *
 * Base interface implemented by page classes that makes sure they are invoked with correct
 * parameters type.
 */
export class CsPage<ParamsType, ResultType = void> implements CsPageComponentInterface<ResultType> {
  navParams: NavParams<ParamsType>;

  /**
   * True while this page is NOT the one on screen: another page covers it, or it has left.
   * Despite the name it does not mean "destroyed" — a covered page, its component and its data
   * all stay alive. Use it to skip WORK after an `await` that should not run while covered.
   * A redraw needs no check (`markForCheck` cannot throw NG0911 in Angular 20), and cs-nav
   * redraws the page when it comes back (#5686).
   *
   * This flag is automatically managed by cs-nav - no need to call super in lifecycle hooks.
   */
  protected viewDestroyed = false;

  /**
   * The page's "this departure was already decided — do not ask about unsaved work" switch.
   *
   * A guarded page arms it with `this.leaveWithoutAsking.set()` on the ways out that have
   * already settled what happens to the edits, and opens its `ionViewCanLeave` with
   * `if (this.leaveWithoutAsking.consume()) return true;`. Reading it clears it — see
   * `OneShotLeaveFlag` above for why that matters.
   */
  protected readonly leaveWithoutAsking = new OneShotLeaveFlag();

  /** Active timeouts that will be cancelled when the view is destroyed. */
  private _pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  isParamsValid(params: ParamsType): boolean {
    void params;
    return true;
  }
  async ionViewCanEnter(): Promise<any | void> {
    return true;
  }

  async ionViewDidEnter() { }

  async ionViewWillEnter(): Promise<void> { }

  async ionViewWillLeave(): Promise<void> { }

  async ionViewDidLeave(): Promise<void> { }

  async ionViewCanLeave(): Promise<CanLeaveResult<ResultType>> {
    return true;
  }

  async ionViewDidReEnter() { }

  constructor() {
    this.navParams = new NavParams({} as ParamsType);
  }

  /**
   * Called by cs-nav before ionViewWillEnter. Marks the page as the one on screen again.
   * cs-nav redraws the page right after this (#5686).
   * @internal Do not call directly - managed by navigation.
   */
  _navPrepareEnter(): void {
    this.viewDestroyed = false;
  }

  /**
   * Called by cs-nav before ionViewWillLeave, including when a page is only covered. Marks the
   * page as not on screen and cancels pending timeouts.
   * @internal Do not call directly - managed by navigation.
   */
  _navPrepareLeave(): void {
    this.viewDestroyed = true;
    this._cancelPendingTimeouts();
  }

  /**
   * Wrapper for setTimeout that is cancelled when the page leaves or is covered, so a delayed
   * callback does not run for a page that is not on screen. Use it instead of raw setTimeout.
   *
   * @example
   * ```typescript
   * this.safeTimeout(() => {
   *   this.animating = false;
   *   this.cd.markForCheck();
   * }, 300);
   * ```
   */
  protected safeTimeout(callback: () => void, delay: number): ReturnType<typeof setTimeout> {
    const timeoutId = setTimeout(() => {
      // remove from pending list
      const index = this._pendingTimeouts.indexOf(timeoutId);
      if (index > -1) this._pendingTimeouts.splice(index, 1);
      // if view destroyed, callback is silently skipped
      if (!this.viewDestroyed) {
        callback();
      }
    }, delay);
    this._pendingTimeouts.push(timeoutId);
    return timeoutId;
  }

  /** Cancel a specific timeout created with safeTimeout. */
  protected cancelTimeout(timeoutId: ReturnType<typeof setTimeout>): void {
    clearTimeout(timeoutId);
    const index = this._pendingTimeouts.indexOf(timeoutId);
    if (index > -1) this._pendingTimeouts.splice(index, 1);
  }

  /**
   * Ask for a redraw of this page. It no longer checks `viewDestroyed`, and that is deliberate (#5686).
   *
   * It used to skip the redraw while the page was covered. That prevented no error:
   * `markForCheck` cannot throw NG0911 in Angular 20, even on a destroyed view. What it did
   * was drop real redraws. On a tablet or desktop a page under a modal stays ON SCREEN, so the
   * user watched stale content for as long as the modal was open. A redraw on a page that is
   * really hidden costs one template check in an update that runs anyway.
   *
   * Kept as a method so the pages calling it need no change. See `design-docs/navigation.md`,
   * "Read `viewDestroyed` for what it is".
   */
  protected safeMarkForCheck(cd: ChangeDetectorRef): void {
    cd.markForCheck();
  }

  private _cancelPendingTimeouts(): void {
    for (const timeoutId of this._pendingTimeouts) {
      clearTimeout(timeoutId);
    }
    this._pendingTimeouts = [];
  }

}

/**
 * Base class for callable pages that can be opened with callAsync() and return a result.
 * Callable pages are "ephemeral" - they're dropped on browser refresh.
 *
 * Usage:
 * ```typescript
 * export class InputCalendarPopover extends CsCallablePage<InputCalendarPageParams, InputCalendarPageResult> {
 *   onOKClick() {
 *     await this.returnAsync({ dateISO: this.date.toISODate() });
 *   }
 * }
 * ```
 */
export class CsCallablePage<ParamsType, ResultType> extends CsPage<ParamsType> {
  // NavController reference - set by cs-nav when page is created
  protected navCtrl: NavController;

  /**
   * Return from this callable page with a result.
   *
   * The result is only WRITTEN DOWN here, not handed over. The caller receives it
   * when this page's component is destroyed, which happens after the caller page
   * is back on screen — see `recordCallResult` for why that matters (#4411).
   */
  async returnAsync(result: ResultType): Promise<void> {
    const callId = this.navParams.get('_callId') as string | undefined;

    // if callId exists, record the result for delivery on destroy
    if (callId) {
      this.navCtrl.recordCallResult(callId, result);
    }

    await this.navCtrl.popAsync();
  }
}

export type CsPageClass<ParamsType> = {
  new(...args: any[]): CsPage<ParamsType>,
};

/**
 * Check if a page class is a callable page (extends CsCallablePage).
 * Callable pages are ephemeral - they're dropped on browser refresh.
 */
export function isCallablePageClass(PageClass: Type<any>): boolean {
  // Walk up the prototype chain to check if it extends CsCallablePage
  let proto = PageClass;
  while (proto && proto !== Object) {
    if (proto === CsCallablePage) {
      return true;
    }
    proto = Object.getPrototypeOf(proto);
  }
  return false;
}

export interface NavOptions {
  updateUrl?: boolean;
  fromBrowser?: boolean; // Indicates navigation initiated by browser back/forward
}

export class NavParams<ParamsType = any> {

  constructor(public data: ParamsType) {
    hydrateLocalParams(data);
  }

  /**
   * Get a parameter value. Prefer using `this.data.key` directly instead.
   * For local params, use `this.data.local?.key`.
   */
  get(param: string): any {
    const dataObj = this.data as Record<string, any>;

    // if data is not an object, return undefined
    if (!dataObj || typeof dataObj !== 'object') {
      return undefined;
    }

    // for ephemeral callable pages, check ephemeral store via _callId
    const callId = dataObj._callId;
    if (callId && ephemeralLocalParams.has(callId)) {
      const storedParams = ephemeralLocalParams.get(callId);
      if (param in storedParams) {
        return storedParams[param];
      }
    }

    // Return the direct value (for URL params)
    if (param in dataObj) {
      return dataObj[param];
    }

    return undefined;
  }

  /**
   * Check if a parameter exists (either directly or in local).
   */
  has(param: string): boolean {
    const dataObj = this.data as Record<string, any>;

    // if data is not an object, return false
    if (!dataObj || typeof dataObj !== 'object') {
      return false;
    }

    if (param in dataObj) {
      return true;
    }

    // Check in local object via localId
    const localId = dataObj.localId;
    if (typeof localId === 'string') {
      const localObj = retrieveLocalParam(localId);
      if (localObj && typeof localObj === 'object' && param in localObj) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a local parameter exists.
   */
  hasLocal<K extends keyof NonNullable<LocalParamsOf<ParamsType>>>(key: K): boolean {
    const dataObj = this.data as Record<string, any>;

    if (!dataObj || typeof dataObj !== 'object') {
      return false;
    }

    const localId = dataObj.localId;
    if (typeof localId === 'string') {
      const localObj = retrieveLocalParam(localId);
      return localObj && typeof localObj === 'object' && (key as string) in localObj;
    }

    return false;
  }
}

export class Page {
  instance: CsPageComponentInterface;
  component: ComponentRef<CsPageComponentInterface>;
  data: any;
  id: string;
}

export type PageFilter = (pageName: string, params?: any) => Promise<string | null>;

/**
 * The logger NavController actually holds. At runtime it is the app's
 * LoggingService, wired in via setLogger() during app init, but it is typed
 * narrowly here so this module does not depend on the concrete service. It
 * extends the pure NavStackLogger converter interface with the two silent-report
 * tiers this file needs (used to surface a back-navigation that timed out), so
 * the shared NavStackLogger interface stays untouched.
 */
interface NavLogger extends NavStackLogger {
  trackSilentProblem(name: string, context: unknown): void;
  trackSilentInfo(name: string, context: unknown): void;
}

/**
 * How long a browser back waits for a page that is still opening before it closes pages
 * (#6411, NavController.targetDepthForBrowserBackAsync). An opening slide takes about 300 ms,
 * so this only runs out when a push is stuck, and the back then closes pages as it always did.
 */
const OPENING_PUSH_WAIT_MS = 3000;

/**
 * Whether the browser-history write for a newly pushed page ran straight away, or had
 * to wait for a browser-initiated navigation to finish first. Only a write that waited
 * reports itself. See NavController.writeBrowserHistoryForPushedPage.
 */
enum PushHistoryWriteTiming {
  Immediate,
  Deferred,
}

/**
 * Whether a popped page's returnCallback runs straight away, or has to wait for a
 * browser-initiated navigation to finish first. See
 * NavController.deliverReturnCallbackAfterPop, whose comment says why waiting matters.
 */
enum ReturnCallbackTiming {
  Immediate,
  AfterBrowserNav,
}

/**
 * Why a pushed page's browser-history write wrote nothing before it reached BrowserHistory.
 * What came of a write that did reach it is BrowserHistory's OpenedPageHistoryOutcome. The
 * values are the strings that reach Sentry and Mixpanel, so renaming one renames it in the
 * reports too. See NavController.reportPushHistoryWrite.
 *
 * `page-no-longer-top` used to be here: a page buried by a later push got no entry at all.
 * Since #6122 such a page gets its entry, so that outcome no longer exists.
 */
enum PushHistoryOutcome {
  PageAlreadyClosed = 'page-already-closed',
  PushAddedNoPage = 'push-added-no-page',
}

@Injectable()
export class NavController {
  navReady = DeferredPromise.defer<boolean>();
  // Resolved when the first initializeNavigationSystem run has finished, whatever its
  // outcome. Until then the start page may still be replaced: Home is shown first, and
  // the pre-show step that follows sets the root again, which drops any page opened on
  // top of it in the meantime. Home waits for it before showing the "Please update"
  // alert (#6770). Never rejected.
  private navigationInitialized = DeferredPromise.defer<void>();
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    public location: Location,
    public serializer: UrlService,
    private breadcrumbService: NavigationBreadcrumbService,
    private pubSub: PubSubService,
  ) {
    // #6493: only entries of pages still on the stack must survive a full sessionStorage. With
    // no navigation component, or while a stack is being restored, the answer is unknown and
    // every entry counts as open.
    configureNavLocalParams({ openLocalParamIds: () => openLocalParamIdsOfStack(this.nav?.pages, this.isRestoringNavStack) });
  }

  nav: CsNavComponent = undefined;
  private browserHistory: BrowserHistory;
  // The real browser, handed to BrowserHistory and to the warm-resume address helpers. Every
  // member reads its global when it is called, so building this here is safe at any point.
  private readonly browserEnv: BrowserEnvironment = realBrowserEnvironment();
  // Flag to indicate navigation stack is being restored (prevents UI flicker during restore)
  private isRestoringNavStack = false;
  // L10 (#3986): set to abandon an in-flight nav restore — the page-load loop checks it between
  // pushes and stops. Reset at the start of each restore. Used by a merely-slow-restore stop and
  // by the Step 12 / L8 navRestoring watchdog.
  private navRestoreAbandoned = false;
  // L10 (#3986): a re-route (e.g. Phase B's unlock setRootAsync) requested while a restore was in
  // flight, deferred so it can't clobber the half-rebuilt stack. Drained in the restore's finally.
  private pendingRerouteAfterRestore: (() => Promise<void>) | null = null;
  // df-53c89a39b0e6: the one owner of "move the person after a server login". See
  // claimPostLoginRoute and requestPostLoginRoute
  private readonly postLoginRoutes = new PostLoginRouteCoordinator(
    {
      // raw setTimeout: the nav controller lives as long as the page does
      set: (callback, ms) => setTimeout(callback, ms),
      clear: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
      now: () => Date.now(),
    },
    {
      onClaimExpired: (reason, heldMs) =>
        this.logging?.trackSilentProblem('PostLoginRouteClaimExpired', { reason, heldMs }),
      onRouteFailed: (reason, error) =>
        this.logging?.trackSilentProblem('PostLoginRouteFailed', { reason, error: describeError(error) }),
    },
  );
  // Stores result and expected callback when app-initiated pop triggers browser back
  private pendingPopResult: { result: any; expectedCallback: string } | undefined = undefined;
  private logging: NavLogger;
  // #4681: set once the end-of-boot "no page on screen" recovery has run, so a default page
  // that also refuses to be shown cannot send us round that recovery forever.
  private bootRecoveryAttempted = false;
  // #4681: set when preShowNavAsync handed the boot over to a navigation the page filter
  // started but nobody awaited. The stack is legitimately empty for a moment afterwards, so
  // the recovery must not read that as a boot that put nothing on screen.
  private navigationHandedOffToPageFilter = false;
  // Stores pending callAsync Promises, plus the result returnAsync recorded for
  // each. Delivered when the callable page's component is destroyed (#4411).
  // The map and the rules about it live in nav-call-registry.ts, where they can be
  // unit tested; this class keeps only the side effects around them.
  private callRegistry = new NavCallRegistry();
  // Duplicate-push guard: prevent double-tap from pushing the same page twice
  private lastPushPageClass: any;
  private lastPushTime = 0;
  // how many pushes have put their page on the stack but not yet returned, so the page has no
  // history entry yet (see pushCountingOpeningAsync)
  private pushesOpening = 0;
  // the same pushes as promises, so a browser back can wait for them to finish (#6411, see
  // targetDepthForBrowserBackAsync)
  private openingPushes = new Set<Promise<Page | undefined>>();
  // pages whose push has returned but whose history write is still queued behind a browser
  // navigation, so they have no history entry yet (see writeBrowserHistoryForPushedPage)
  private pagesAwaitingDeferredHistory = new WeakSet<Page>();
  // When true, the app is about to reload — all navigation attempts should be ignored
  reloadPending = false;

  // Callbacks deferred from browser-initiated pops. When a popAsync return callback
  // (e.g. handleFoodPageResult → addIngredientToListAsync → pushAsync) runs during
  // the popstate handler, BrowserHistory.pushState skips because a popstate is being handled,
  // and the pop loop in handleBrowserNavigation may undo the push. Deferring the callback
  // until after the popstate handler completes (BrowserHistory back to Idle) fixes both issues.
  private _postBrowserNavCallbacks: (() => void)[] = [];

  queuePostBrowserNavCallback(callback: () => void) {
    this._postBrowserNavCallbacks.push(callback);
  }

  flushPostBrowserNavCallbacks() {
    const callbacks = this._postBrowserNavCallbacks;
    this._postBrowserNavCallbacks = [];
    runPostBrowserNavCallbacks(callbacks, this.logging);
  }

  /**
   * Whether a back navigation started by the browser is being handled right now.
   *
   * A page's `ionViewCanLeave` that wants to navigate somewhere else after the leave asks this
   * first: during a browser back the history entry has already moved, so it must refuse the
   * leave and queue its navigation with `queuePostBrowserNavCallback` instead. See
   * `finishDayErrandOnLeave` in `pages/foods/add-food-errand.ts`.
   */
  isBrowserNavigationInProgress(): boolean {
    return this.browserHistory?.isFromBrowser() ?? false;
  }

  pageFilter: PageFilter = async (page: string) => page;

  /**
   * Set the logger for tracking abnormal navigation errors.
   * Called during app initialization to avoid circular dependency.
   */
  setLogger(logger: NavLogger): void {
    this.logging = logger;
    this.breadcrumbService.setLogger(logger);
    // once per page load: a long editing session can recover many times, and one report says
    // the condition happens
    let quotaRecoveryReported = false;
    configureNavLocalParams({
      // once per condition per page load: a damaged tab can hit the same entry on every navigation
      reportDamage: createOncePerConditionReporter((damage, err) => {
        const context = { error: describeError(err) };
        if (damage === LocalParamDamage.Evicted) {
          logger.trackSilentInfo(damage, context);
        } else {
          logger.trackSilentProblem(damage, context);
        }
      }),
      reportQuotaRecovery: recovery => {
        if (quotaRecoveryReported) return;
        quotaRecoveryReported = true;
        logger.trackSilentInfo('NavLocalParamQuotaRecovered', { ...recovery });
      },
    });
  }

  /**
   * @internal The logger BrowserHistory uses to report app-initiated
   * back-navigation outcomes. Undefined until setLogger() runs during app init,
   * so callers MUST guard against a missing value.
   */
  getNavLogger(): NavLogger | undefined {
    return this.logging;
  }

  /**
   * Build current trail from the navigation stack
   */
  private getCurrentTrail(): string {
    if (!this.nav || !this.nav.pages || this.nav.pages.length === 0) {
      return '';
    }

    // Use the unified converter to get breadcrumb pages
    const breadcrumbPages = navStackToBreadcrumb(this.nav.pages, this.logging);

    // Convert to URL format and remove leading slash
    const url = pagesToUrl(breadcrumbPages);
    return url.substring(1);
  }

  /**
   * Build a fallback trail for a page using its defaultHistory
   */
  private buildFallbackTrail(pageName: string, params?: any): string {
    const defaultHistory = getPageDefaultHistory(pageName);
    const pageStack: PageStackEntry[] = [];

    // Add default history pages first
    if (defaultHistory && defaultHistory.length > 0) {
      for (const historyPageClass of defaultHistory) {
        const historyPageSegment = getPageSegment(historyPageClass);
        if (historyPageSegment) {
          pageStack.push({
            pageName: historyPageSegment,
            params: {}
          });
        }
      }
    }

    // Add the current page (convert class name to segment)
    const currentPageClass = getPageClass(pageName);
    const currentPageSegment = currentPageClass ? getPageSegment(currentPageClass) : pageName;
    pageStack.push({
      pageName: currentPageSegment,
      params: params || {}
    });

    return this.breadcrumbService.encodeBreadcrumbs(pageStack);
  }

  isPageCurrent(name: string): boolean {
    // check if page with given name is at top of navigation stack
    if (!this.nav || !this.nav.pages || this.nav.pages.length === 0) {
      return false;
    }
    const activePage = this.nav.getActive();
    if (!activePage) {
      return false;
    }
    const activePageName = getPageName(activePage.component.componentType);
    return activePageName === name;
  }

  /**
   * Check if navigation stack is currently being restored (e.g., after page refresh).
   * UI components can use this to avoid showing/animating during restoration.
   */
  isRestoring(): boolean {
    return this.isRestoringNavStack;
  }

  getPageBySelector(selector: string): string {

    for (let page of getAllDeepLinks()) {

      let factory = this.componentFactoryResolver.resolveComponentFactory(page.component);
      if (factory.selector === selector) {
        return page.name;
      }
    }

    return undefined;
  }

  /**
   * The address for a root page named by page name, or undefined when the page is
   * not in the deep-link registry.
   *
   * One place rather than four: every site that sets a root page has to write the
   * same address, and #5124 was the second time one of them was fixed while the
   * others went on dropping parameters. `pageRootUrl` stays segment-based so it can
   * be unit-tested without the registry; this is the registry lookup around it.
   */
  private rootUrlForPage(pageName: string, params: any): string | undefined {
    const pageSegment = getPageHead(pageName);
    return pageSegment ? pageRootUrl(pageSegment, params, this.logging) : undefined;
  }

  /**
   * Write the address for a page that sits alone at the root of the stack, replacing
   * the current history entry. A root page has nothing above it, so its breadcrumb
   * trail is empty and the page stack recorded beside the address is just itself.
   *
   * The caller checks `browserHistory` is there and decides what to do when it is not,
   * because preShowNavAsync has a fallback for that case and the other callers do not.
   */
  private replaceHistoryWithRootPage(pageName: string, params: any): void {
    const url = this.rootUrlForPage(pageName, params) ?? '/';
    this.browserHistory.replaceState(this.serializer.normalizeUrl(url), [pageName], '');
  }

  /**
   * Read the live navigation stack and return both the converted pages and the address
   * that represents them. Every method that tells the browser "the stack changed" needs
   * that same pair.
   *
   * Returning both rather than just the address is deliberate: navStackToPages reports a
   * non-serializable page parameter to Sentry as it walks the stack, so a caller that
   * needs the pages as well must not call it a second time or the same parameter is
   * reported twice.
   *
   * `pagesToUrl` already answers '/' for an empty stack and always returns a string that
   * starts with '/' and never ends with one, so `normalizeUrl` cannot change its output
   * and no empty-stack fallback is needed around it.
   */
  private pagesAndUrlForLiveNavStack(): { pages: PageWithParams[]; url: string } {
    const pages = navStackToPages(this.nav.pages, this.logging);
    return { pages, url: pagesToUrl(pages) };
  }

  /**
   * Internal helper for setting root with a page class directly.
   * Used when we already have the class and don't need a lookup.
   */
  private async internalSetRootWithClassAsync<T>(PageClass: CsPageClass<T>, params?: any, opts?: NavOptions) {
    if (this.nav) {
      await this.nav.setRoot(PageClass, params);
    } else {
      const pageName = getPageName(PageClass);
      if (opts) {
        console.error(`Nav: Opts for ${pageName} ignored because nav is hidden!`);
      }

      // with no nav view there is no page object to hold the params, so the address IS
      // the only carrier — preShowNavAsync reads it back when the nav view appears (#5124)
      const path = this.rootUrlForPage(pageName, params) ?? getPageFullPath(pageName);
      console.assert(!!path);
      this.location.replaceState(path, undefined, {});
    }
  }

  /**
   * Internal helper for setting root with a page name string.
   * Used for redirects from pageFilter and internal URL-based navigation.
   */
  async internalSetRootAsync(page: string, params?: any, opts?: NavOptions) {
    if (this.nav) {
      const pageMetadata = getAllDeepLinks().find(p => p.name === page);
      if (!pageMetadata) {
        console.error(`Nav: Page ${page} not found in deep links registry`);
        throw new Error(`Page ${page} not found`);
      }
      await this.nav.setRoot(pageMetadata.component, params);

      // Emit navigation change event
      this.emitNavigationChange();

      // Update browser history if this is app-initiated navigation
      if (!opts?.fromBrowser && this.browserHistory) {
        const pageStack = [page];
        const breadcrumbs = ''; // Root page has empty trail
        // built from the live nav stack, not serializeComponent: the latter emits no
        // params at all, so it wrote a param-less address and the next warm resume
        // restored a blank page (#5124).
        // Same reasoning, same shape, as setRootAsync below.
        const { url } = this.pagesAndUrlForLiveNavStack();
        this.browserHistory.replaceState(url, pageStack, breadcrumbs);
      }
    } else {
      if (opts) {
        console.error(`Nav: Opts for ${page} ignored because nav is hidden!`); // this is almost never used?
      }

      // with no nav view there is no page object to hold the params, so the address IS
      // the only carrier — preShowNavAsync reads it back when the nav view appears. This
      // is the path a deep link tapped while the app is closed takes, and writing the
      // params here is what makes a shared recipe link open its recipe (#5124).
      const path = this.rootUrlForPage(page, params) ?? getPageFullPath(page);
      console.assert(!!path);
      this.location.replaceState(path, undefined, {});
    }
  }

  /**
   * Show the page the current address names, once the nav view exists.
   *
   * EVERY address this method writes is built with `rootUrlForPage`, never with
   * `serializer.serializeComponent` and never by substituting `:name` into a route path.
   * Both of those drop every parameter, which loses the page a warm resume would restore.
   * See design-docs/navigation.md, "Root-page URL serialization".
   */
  async preShowNavAsync() {
    console.log('Nav: preShowNavAsync called');
    // cleared per call: a hand-off on an earlier call must not go on suppressing the
    // end-of-boot recovery for a later one that really did leave nothing on screen
    this.navigationHandedOffToPageFilter = false;
    let currentPage = this.getCurrentPage();

    // Parse params and trail from current URL
    const currentUrl = this.location.path();
    console.log('Nav: Parsing URL:', currentUrl);
    const segment = this.serializer.parse(currentUrl);
    const params = segment?.data;
    const breadcrumbs = segment?.breadcrumbs;
    console.log('Nav: Parsed segment:', { params, breadcrumbs, currentPage });
    
    // if unknown url, let the page filter decide what to do
    if (!currentPage) {
      // host and path only: the community sign-in returns to an address whose query IS a
      // forum login for that person, and this line lands in Sentry breadcrumbs and in the
      // console log the E2E run publishes as a build artifact (#4525, #5151).
      console.log(`Nav: Unrecognized url "${safeUrlForLog(location.href)}", consulting page filter`);
      const filteredPage = await this.resolvePageFilterAsync(undefined);
      // if page filter returns null, it means an external redirect is happening
      if (filteredPage === null) {
        console.log('Nav: Page filter initiated external redirect, stopping navigation');
        // The filter has taken over: for a signed-out visitor it has already STARTED the
        // login or landing page without waiting for it (see pageFilterAsync in
        // app.component.ts). A page is on its way, so the end-of-boot backstop must not read
        // the momentarily-empty page list as a boot that failed to show anything.
        //
        // This covers the signed-out case only. The other hand-off the filter can make — a
        // pending coach-manage, which reloads the app — arrives at the "app is reloading"
        // return further down instead, and the backstop's own `reloadPending` check is what
        // covers that one. See recoverIfBootLeftNoPageAsync (#4681).
        this.navigationHandedOffToPageFilter = true;
        return;
      }
      currentPage = filteredPage || 'HomePage';

      // Update URL to reflect where we're going. No params to write: the url this page
      // was resolved FROM was not recognized, so its parsed params belong to nothing
      if (this.browserHistory) {
        this.replaceHistoryWithRootPage(currentPage, null);
      }
    }

    // go to initial page
    let newPage = await this.resolvePageFilterAsync(currentPage, params);
    if (newPage == null) {
      console.log('Nav: Page filter returned null during pre-show — app is reloading, stopping navigation');
      return;
    }
    if (newPage !== currentPage) {
      console.log(`Nav: Page filter redirected from ${currentPage} to ${newPage} during pre-show`);
      currentPage = newPage;

      // Update the URL to reflect the redirect with params
      if (this.browserHistory) {
        this.replaceHistoryWithRootPage(newPage, params);
      } else {
        // Fallback if browserHistory not yet available (shouldn't happen but just in case).
        // Same shape as internalSetRootAsync's no-nav-view branch: this used to substitute
        // `:name` placeholders into the page's route path, which no path declares (#5124)
        const path = this.rootUrlForPage(newPage, params) ?? getPageFullPath(newPage);
        if (path) {
          this.location.replaceState(path, undefined, {});
        }
      }
    }

    // Restore navigation stack from breadcrumbs if present
    if (breadcrumbs && breadcrumbs.trim() !== '') {
      console.log(`Nav: Restoring navigation stack from breadcrumbs: ${breadcrumbs}`);
      const pageStack = this.breadcrumbService.decodeBreadcrumbs(breadcrumbs);

      if (pageStack.length > 0) {
        // Set up the full navigation stack
        await this.restoreNavigationStack(pageStack, currentPage, params);

        // Emit navigation change event
        this.emitNavigationChange();

        return;
      }
    }

    // Check if we actually have the expected page loaded in the nav stack
    const activePage = this.nav && this.nav.getActive();
    const activePageName = activePage ? getPageName(activePage.component.componentType) : null;
    
    if (activePageName === currentPage && this.isPageCurrent(currentPage)) {
      console.log(`Nav: Page ${currentPage} is already current, using default initialization`);
      
      // But still update the URL if it's empty or just '/'
      const currentPath = this.location.path();
      if (!currentPath || currentPath === '/' || currentPath === '') {
        if (this.browserHistory) {
          this.replaceHistoryWithRootPage(currentPage, params);
        }
      }

      // if loadViewForSegment built the pages under this one from defaultHistory, that stack
      // survives here with the one history entry boot wrote, so give each page its own entry
      // or Back leaves the app (#6413; design-docs/navigation.md, "One browser history entry
      // per open page"). A single page writes nothing.
      this.buildBrowserHistoryForStack();

      return;
    }

    console.log(`Nav: Starting at page ${currentPage}`);
    await this.internalSetRootAsync(currentPage, params);

    // record the page stack alongside the address, built the same way internalSetRootAsync
    // just built it (the note above this method says why every write here is built that way)
    if (this.browserHistory) {
      this.replaceHistoryWithRootPage(currentPage, params);
    }
  }

  setNav(nav: CsNavComponent) {
    this.nav = nav;
    this.navReady.resolve(nav != undefined);
  }

  /**
   * Initialize the navigation system in one place.
   */
  async initializeNavigationSystem(overlayStack: OverlayStackService) {
    // the start-up sends the person through the page filter, so it is a post-login move of its
    // own: a page asking to move the person meanwhile waits for it (df-53c89a39b0e6)
    const routeClaim = this.postLoginRoutes.claim('navigation start-up');
    try {
      await this.initializeNavigationSystemInnerAsync(overlayStack);
    } finally {
      this.navigationInitialized.resolve();
      routeClaim.release();
    }
  }

  /**
   * df-53c89a39b0e6: a part of the app is about to move the person after a server login (warm
   * resume Phase B, a login retry). Release the claim once that move is over; a page that asked
   * meanwhile is moved only if it is still on screen. See post-login-route-coordination.ts.
   */
  claimPostLoginRoute(reason: string): PostLoginRouteClaim {
    return this.postLoginRoutes.claim(reason);
  }

  /**
   * df-53c89a39b0e6: a page would like the person moved after a server login. Runs now when
   * nothing else is moving the person, otherwise once that is over and only if `stillWanted`
   * (the page is still on screen); when it is dropped instead, `onDropped` runs. Goes through
   * rerouteRespectingRestore (L10, #3986).
   */
  requestPostLoginRoute(reason: string, route: () => Promise<void>, stillWanted: () => boolean, onDropped: () => void): void {
    this.postLoginRoutes.request(reason, () => this.rerouteRespectingRestore(route), stillWanted, onDropped);
  }

  /**
   * Waits until the start-up navigation has settled: the first initializeNavigationSystem
   * run is over, so the page on screen will no longer be replaced by boot. A page that
   * wants to open something over itself from its first ionViewDidEnter waits for this first.
   */
  async whenNavigationInitializedAsync(): Promise<void> {
    await this.navigationInitialized;
  }

  private async initializeNavigationSystemInnerAsync(overlayStack: OverlayStackService) {
    // destroy previous instance's popstate listener before creating a new one
    this.browserHistory?.destroy();

    // warm resume: restore the persisted URL if the current URL is empty (process kill resets to '/').
    // Gate on a warm-resume (logged-in) session: warm_resume_uid is set only when a
    // signed-in user's auth was cached. An anonymous visitor hitting the root must
    // fall through to the cold-start landing redirect (app.component pageFilter) and
    // NOT have a stale login/subscribe URL restored over the top of it — otherwise
    // the root URL shows the sign-in form instead of the landing page.
    const currentPath = this.location.path();
    if (shouldRestorePersistedNavUrl({ currentPath, hasWarmResumeSession: getWarmResumeUid() != null })) {
      const persistedUrl = getPersistedUrl(this.browserEnv);
      if (persistedUrl && persistedUrl !== '/') {
        console.log(`NavController: Restoring persisted URL for warm resume: ${persistedUrl}`);
        this.location.replaceState(persistedUrl);
      }
    }

    this.browserHistory = new BrowserHistory(
      this, overlayStack, this.location, this.serializer, this.breadcrumbService, this.browserEnv);
    this.browserHistory.init();

    // Handle initial page load from URL
    const browserUrl = this.serializer.normalizeUrl(this.location.path());
    console.debug(`NavController: Initial load from URL: ${browserUrl}`);
    
    // Parse the URL to get the segment
    const segment = this.serializer.getCurrentSegment(browserUrl);

    // Check if there's a trail to restore before loading the view
    if (segment?.breadcrumbs && segment.breadcrumbs.trim() !== '') {
      console.log(`Nav: Found breadcrumbs during initialization: ${segment.breadcrumbs}`);
      // Let preShowNavAsync handle trail restoration instead of loadViewForSegment
      await this.preShowNavAsync();
    } else {
      // Load the view for this segment (initial page load without trail)
      if (segment) {
        await this.loadViewForSegment(segment);
      }

      // Run preShowNavAsync to handle any redirects or filters
      await this.preShowNavAsync();
    }

    await this.recoverIfBootLeftNoPageAsync();
  }

  /**
   * Last resort at the end of boot: if nothing managed to put a page on screen, send the user
   * to the default page instead of leaving them looking at an empty app.
   *
   * This happens when the app is opened straight from a link and that page refuses to be
   * shown — its ionViewCanEnter throws, cs-nav cancels the navigation and restores the
   * previous page list, and on a cold start that list is empty. The user is then stuck: there
   * is no page, and nothing behind it to go back to. Worse, the address stays remembered, so
   * every relaunch for the next few hours lands in the same place. That is #4681, reported by
   * a customer who could only escape by wiping the app's data.
   *
   * It runs here, at the end of boot, rather than inside the cancel path itself, because by
   * this point every boot navigation has finished — so it cannot collide with one still in
   * flight. NavController already recovers the same way for an unrestorable deep URL (see
   * restoreNavigationStack's missing-local-id branch).
   */
  private async recoverIfBootLeftNoPageAsync() {
    if (this.nav?.getActive()) {
      return;
    }
    // A navigation the page filter started, but nobody waited for, is still on its way. This is
    // the ORDINARY signed-out boot: the filter kicks off the login or landing page and returns
    // without awaiting it, so the stack really is empty at this instant and nothing is wrong.
    // Recovering here would file a false report AND start a second, competing navigation.
    if (this.navigationHandedOffToPageFilter) {
      return;
    }
    // A reload is already coming, and every navigation is ignored while it is pending — so
    // there is nothing useful to do and no problem to report.
    if (this.reloadPending) {
      return;
    }
    // One shot only. If the default page itself cannot be shown, a second attempt would fail
    // the same way and we would loop through boot recovery forever.
    if (this.bootRecoveryAttempted) {
      // The worst outcome this recovery has: the app is left with no page on screen and we
      // have run out of ways to fix it. Report it — a console line alone never reaches us.
      this.logging?.trackSilentProblem('BootRecoveryExhausted', { url: this.location.path() });
      console.error('Nav: boot left no page on screen and the recovery already ran — giving up');
      return;
    }
    this.bootRecoveryAttempted = true;

    // Forget the remembered address FIRST. The recovery navigation below overwrites it anyway,
    // but if that navigation fails we must still not send the user back to the same dead page
    // on their next launch — which is the part the customer in #4681 actually suffered from.
    clearPersistedUrl(this.browserEnv);

    this.logging?.trackSilentProblem('BootEndedWithNoPage', { url: this.location.path() });
    console.error('Nav: boot ended with no page on screen — routing to the default page');
    await getMyApp().gotoDefaultPageAsync();
  }

  /**
   * Load a view for a given segment from URL
   */
  private async loadViewForSegment(segment: NavSegment) {
    if (!segment) {
      return;
    }

    // build the whole stack for this page and replace whatever is there.
    // df-e7459e7b901c: a loop here used to look for an already-open page to pop back to
    // instead, but it compared `Page.id` (which starts with '/') against `segment.id` (which
    // never does) and so could never match. It was deleted rather than repaired: `segment.id`
    // also carries the page's parameters and `Page.id` does not, so a working match would be
    // a new, untested startup shortcut rather than a fix.
    let pages: { page: string | Type<CsPageComponentInterface<any>>, params: any }[] = [];

    // Use trail if available, otherwise fall back to defaultHistory
    if (segment.breadcrumbs && this.breadcrumbService.validateBreadcrumbs(segment.breadcrumbs)) {
      // Decode trail to build page stack
      const trailPages = this.breadcrumbService.decodeBreadcrumbs(segment.breadcrumbs);
      for (const trailPage of trailPages) {
        const pageClass = getPageClassBySegment(trailPage.pageName);
        if (pageClass) {
          pages.push({
            page: pageClass,
            params: trailPage.params
          });
        }
      }
    } else {
      // if the page filter sends this page somewhere else, do not manufacture the pages
      // under it: the redirect target would be opened once per page, and its top copy
      // would show a Back arrow that leaves the app (#6383; design-docs/navigation.md,
      // "defaultHistory"). preShowNavAsync runs next and sets the target as the one root
      if (segment.defaultHistory?.length) {
        const filteredPage = await this.resolvePageFilterAsync(segment.name, segment.data);
        // if the filter stops navigation (a coachManage link is reloading the app), build
        // nothing either. Asking has already started that reload, so preShowNavAsync's own
        // question is answered as if no link were pending and may show a page until then
        if (filteredPage == null) {
          return;
        }
        if (filteredPage !== segment.name) {
          return;
        }
      }

      // Fall back to defaultHistory
      if (segment.defaultHistory) {
        for (let historyPage of segment.defaultHistory) {
          pages.push(
            {
              page: historyPage,
              params: {}
            }
          );
        }
      }

      // Then add the current page on top
      pages.push({
        page: segment.name,
        params: segment.data,
      });
    }
    
    return this.setPagesAsync(pages, { updateUrl: false });
  }

  /** The filter is invoked before any navigation, and if it returns a different page than passed in,
  the app will be redirected straight to setting this page as a root. (I.e. it will never be pushed.)
  This is used to force login page and similar if the user arrives via some external link, or edits the URL.
  
  NOTE: The filter will be re-invoked on the new page, and this will be repeated until it returns the 
  same page. It is ok to use this for  cascade re-routing. But note that it might cause infinite 
  recursion if not contained.
   */
  setPageFilter(pageFilter: PageFilter) {
    this.pageFilter = pageFilter;
  }

  async resolvePageFilterAsync(orgPage: string, params?: any): Promise<string | null> {
    let page = orgPage;
    for (let i = 0; i < 10; i++) {
      let filteredPage = await this.pageFilter(page, params);
      // if filter returns null, it signals "stop navigation" (e.g. app reload in progress)
      if (filteredPage == null) {
        return null;
      }
      if (filteredPage == page) {
        return page;
      }
      page = filteredPage;
    }
    console.error(`Nav: Failed filtering ${orgPage}. Too many retries. Last value: ${page}`);
    return orgPage;
  }

  getCurrentPage() {
    return getPageNameByPath(this.location.path());
  }

  /**
   * The URL segment of the page on screen, or an empty answer when there is none.
   * BrowserHistory needs this for its first breadcrumb and asks for it here, because working
   * it out needs `app/app-page-helpers` — an import tree that would make browser-history.ts
   * impossible to load in a unit test (df-8458e2ecf8ec).
   */
  getCurrentPageSegment(): string {
    return getPageSegment(getPageClass(this.getCurrentPage()));
  }

  /** Has to be called by each page in ionViewCanEnter() */
  async pageGuardAsync(page: string) {
    console.assert(getPageClass(page) !== undefined);
    let filteredPage = await this.resolvePageFilterAsync(page);
    // if filter returns null, app is reloading — stop navigation
    if (filteredPage == null) {
      throw Error('App is reloading');
    }
    if (filteredPage !== page) {
      console.log(`Nav: Page filter redirected from ${page} to ${filteredPage} during pageGuard.`);
      await this.internalSetRootAsync(filteredPage);
      throw Error('Redirected page');
    }
  }

  /**
   * Set a new root page, clearing the navigation stack.
   *
   * @param pageRef - A PageRef constant (e.g., IHomePage)
   * @param params - Navigation parameters (type-checked against the page's param type)
   * @param opts - Navigation options
   */
  async setRootAsync<PageParamsType extends ValidNavParams<PageParamsType>>(pageRef: PageRef<PageParamsType, any>, params?: PageParamsType, opts?: NavOptions): Promise<any> {
    if (this.reloadPending) return;

    const pageName = getPageName(pageRef);
    const PageClass = getPageClass(pageRef);
    console.log(`Nav: Set root ${pageName}`);

    const newPage = await this.resolvePageFilterAsync(pageName, params);
    // if filter returns null, app is reloading — bail
    if (newPage == null) {
      console.log(`Nav: setRoot ${pageName} aborted — app is reloading`);
      return;
    }
    // if redirected, use internalSetRootAsync with the redirected page name
    if (newPage !== pageName) {
      console.log(`Nav: Page filter redirected from ${pageName} to ${newPage} during setRoot`);
      return this.internalSetRootAsync(newPage, params, opts);
    }

    if (this.isPageCurrent(pageName)) {
      console.log(`Nav: Page ${pageName} is already current, ignoring setRoot`);
      return;
    }

    // Process _local parameters - store in sessionStorage, keep IDs in URL params
    const pageId = generateLocalParamId();
    const { urlParams } = processParamsForNavigation(params, pageId);

    // Use PageClass directly for setting root - no lookup needed
    const result = await this.internalSetRootWithClassAsync(PageClass, urlParams, opts);

    // Emit navigation change event
    this.emitNavigationChange();

    // Update browser history if this is app-initiated navigation.
    // Build the URL from the live nav stack — the same path push/pop use — rather
    // than serializeComponent. serializeComponent emits NO params at all — it builds
    // a bare page segment, a page identity rather than an address — so it silently
    // dropped ALL params (e.g. login's
    // `mode`). navStackToPages serializes every URL-safe param, so the param lands
    // in the address bar and survives a refresh / warm-resume restore. When the nav
    // view is hidden there is no stack to read, so pageRootUrl builds the same shape
    // from the page's segment and its params — serializeComponent is not used on
    // either side, and putting it back would drop the params again (#5124).
    if (!opts?.fromBrowser && this.browserHistory) {
      const pageStack = [pageName];
      const breadcrumbs = ''; // Root page has empty trail
      let url: string;
      if (this.nav) {
        url = this.pagesAndUrlForLiveNavStack().url;
      } else {
        // no nav stack to read, so build the same shape from the page's own segment (#5124)
        const rootUrl = this.rootUrlForPage(pageName, urlParams);
        url = rootUrl ? this.serializer.normalizeUrl(rootUrl) : '/';
      }
      this.browserHistory.replaceState(url, pageStack, breadcrumbs);
    }

    return result;
  }

  /**
   * Push a new page onto the navigation stack.
   *
   * IMPORTANT: Navigation parameters are serialized to the URL. Use serializable types:
   * - Strings, numbers, booleans work directly
   * - Dates: Pass as ISO strings (e.g., `date.toISO()`), not DateTime objects
   * - Complex objects: Use `_local` suffix to store in sessionStorage (see processParamsForNavigation)
   * - Functions: Use returnCallback pattern (see CsPage.returnCallback)
   *
   * @param pageRef - A PageRef constant (e.g., IHomePage)
   * @param params - Navigation parameters (type-checked against the page's param type)
   * @param opts - Navigation options
   */
  async pushAsync<PageParamsType extends ValidNavParams<PageParamsType>>(pageRef: PageRef<PageParamsType, any>, params?: PageParamsType, opts?: NavOptions): Promise<any> {
    if (this.reloadPending) return;

    const pageName = getPageName(pageRef);
    const PageClass = getPageClass(pageRef);

    // if double-tap pushed the same page within 500ms, ignore
    const now = Date.now();
    if (PageClass === this.lastPushPageClass && now - this.lastPushTime <= 500) {
      console.log(`Nav: Push ${pageName} ignored (duplicate within 500ms)`);
      return;
    }
    this.lastPushPageClass = PageClass;
    this.lastPushTime = now;

    console.log(`Nav: Push ${pageName}`);

    const newPage = await this.resolvePageFilterAsync(pageName, params);
    // if filter returns null, app is reloading (e.g. coach manage deep link) — bail
    if (newPage == null) {
      console.log(`Nav: Push ${pageName} aborted — app is reloading`);
      return;
    }
    if (newPage !== pageName) {
      console.log(`Page filter redirected from ${pageName} to ${newPage} during push`);
      return this.internalSetRootAsync(newPage);
    }

    // Process _local parameters - store in sessionStorage, keep IDs in URL params
    const pageId = generateLocalParamId();
    const { urlParams } = processParamsForNavigation(params, pageId);

    // Use PageClass directly - no lookup needed
    const openedPage = await this.pushCountingOpeningAsync(PageClass, urlParams);

    // Emit navigation change event
    this.emitNavigationChange();

    // Update browser history if this is app-initiated navigation
    if (!opts?.fromBrowser && this.browserHistory) {
      this.writeBrowserHistoryForPushedPage(openedPage);
    }

    // callers have always received nothing from a push; the opened page is for the history
    // write only
    return undefined;
  }

  /**
   * `CsNavComponent.push`, counted in `pushesOpening` for as long as it runs. The pushed page
   * is on the stack for that whole time but has no history entry until the push returns, which
   * is the window `applyCurrentPageParamsAsync` must recognise (df-19cb4bb87306).
   */
  private async pushCountingOpeningAsync(PageClass: Type<CsPageComponentInterface<any>>, urlParams: any): Promise<Page | undefined> {
    this.pushesOpening++;
    // started inside the try, so a push that throws before it returns a promise (no nav
    // component after a sign-out) still brings the count back down
    let push: Promise<Page | undefined> | undefined;
    try {
      push = this.nav.push(PageClass, urlParams);
      this.openingPushes.add(push);
      return await push;
    } finally {
      this.pushesOpening--;
      if (push) {
        this.openingPushes.delete(push);
      }
    }
  }

  /**
   * Write the browser-history entries for a page that has just been pushed onto the
   * stack, waiting for a browser-initiated navigation to finish first when one is
   * still being handled.
   *
   * The browser must hold one history entry per open page. How the write keeps that true
   * when pages open on top of each other (#6122), why it cannot run during a browser
   * navigation, and what the deferral does about it, is in `design-docs/navigation.md`
   * under "One browser history entry per open page". Deferred finding df-7e2158ed3692.
   *
   * @param openedPage the page this push added and opened, as CsNavComponent.push returned
   *   it — NOT whatever is on top of the stack now. Another push can land on top while this
   *   one's transition runs, and the top page then belongs to that push (#6122). Undefined
   *   when the push opened no page (re-entering the same page with the same params, or a
   *   page whose ionViewCanEnter refused): writing an entry then would leave the browser one
   *   entry ahead of the open pages, so the next back press appears to do nothing. Both the
   *   immediate and the deferred write decline on that (df-7b99c5ac3435).
   */
  private writeBrowserHistoryForPushedPage(openedPage: Page | undefined): void {
    if (!this.browserHistory) {
      return;
    }

    if (this.browserHistory.isFromBrowser()) {
      // until the queued write runs the page has no entry, so a param update meanwhile must not
      // rewrite the current entry to cover it (df-9b9de115255a). Released whichever way the
      // write ends.
      if (openedPage != null) {
        this.pagesAwaitingDeferredHistory.add(openedPage);
      }
      this.queuePostBrowserNavCallback(() => this.applyDeferredBrowserHistoryForPushedPage(openedPage));
      return;
    }

    this.applyBrowserHistoryForPushedPage(openedPage, PushHistoryWriteTiming.Immediate);
  }

  /**
   * The queued history write, which releases the page's mark however the write ends. See
   * writeBrowserHistoryForPushedPage.
   */
  private applyDeferredBrowserHistoryForPushedPage(openedPage: Page | undefined): void {
    try {
      this.applyBrowserHistoryForPushedPage(openedPage, PushHistoryWriteTiming.Deferred);
    } finally {
      if (openedPage != null) {
        this.pagesAwaitingDeferredHistory.delete(openedPage);
      }
    }
  }

  /**
   * The history write itself. See writeBrowserHistoryForPushedPage.
   */
  private applyBrowserHistoryForPushedPage(openedPage: Page | undefined, timing: PushHistoryWriteTiming): void {
    // the push may have opened no page at all — see writeBrowserHistoryForPushedPage's note.
    // Both timings decline on it; only the deferred one reports it.
    if (openedPage == null) {
      if (timing === PushHistoryWriteTiming.Deferred) {
        this.reportPushHistoryWrite(timing, undefined, this.nav?.pages, PushHistoryOutcome.PushAddedNoPage);
      }
      return;
    }

    // the page may have been closed again before this write ran: the pop loop in
    // handleBrowserNavigation re-reads the stack length each time round, so it pops whatever
    // is on top, including a page pushed while it was running. An entry for a closed page
    // would leave the browser one entry ahead, so write nothing.
    const pages = this.nav?.pages;
    const stackPosition = pages != null ? pages.indexOf(openedPage) : -1;
    if (stackPosition < 0) {
      if (timing === PushHistoryWriteTiming.Deferred) {
        this.reportPushHistoryWrite(timing, openedPage, pages, PushHistoryOutcome.PageAlreadyClosed);
      }
      return;
    }

    // the page may also no longer be on top, because another push landed on it while its
    // transition ran. That is fine: BrowserHistory writes the entries from the root up to
    // this page that the browser does not hold yet, and never any above it (#6122).
    const write = this.browserHistory.writeEntriesForOpenedPage(this.historyEntriesForOpenPages(pages), stackPosition);

    // report only AFTER the writes, so the outcome says what happened rather than what was
    // about to be attempted. A deferred write always reports, because that path is rare and
    // the report is how anyone will know how it divides. An immediate write reports only
    // when it was not the ordinary single entry, which is how overlapping pushes are counted.
    if (timing === PushHistoryWriteTiming.Deferred || write.outcome !== OpenedPageHistoryOutcome.EntryWritten) {
      this.reportPushHistoryWrite(timing, openedPage, pages, write.outcome, write.entriesAdded);
    }
  }

  /**
   * One browser-history entry per open page, root first. The entry for the page at index k
   * describes the stack from the root up to and including that page, which is what the
   * browser must show when the user has gone back to it. Pushes, calls and a restored stack
   * (buildBrowserHistoryForStack) all build their entries here.
   */
  private historyEntriesForOpenPages(pages: Page[]): HistoryEntry[] {
    const allPages = navStackToPages(pages, this.logging);
    const pageNames = this.historyPageNames(pages);
    return pages.map((_page, index) => ({
      url: pagesToUrl(allPages.slice(0, index + 1)),
      pageNames: pageNames.slice(0, index + 1),
      // the trail is the address of the pages under this one, without its leading slash
      breadcrumbs: pagesToUrl(allPages.slice(0, index)).substring(1),
    }));
  }

  /**
   * The page list a history entry records for these pages: each page's registered name, root
   * first. Every history writer uses this one format, because BrowserHistory compares an
   * existing entry's last page with it, and handleBrowserNavigation counts the list to decide
   * how many pages to pop.
   */
  private historyPageNames(pages: Page[]): string[] {
    return pages.map(p => getPageName(p.component?.componentType));
  }

  /**
   * Report a page's browser-history write that was not the ordinary case: one that waited
   * for a browser navigation, or an immediate one that was not a single added entry. Every
   * outcome is settled when reported, so info tier, except a write BrowserHistory refused,
   * which leaves the page with no entry: problem tier. Report names, outcomes and why:
   * `design-docs/navigation.md`, "One browser history entry per open page", under "Reports".
   */
  private reportPushHistoryWrite(
    timing: PushHistoryWriteTiming,
    openedPage: Page | undefined,
    pages: Page[] | undefined,
    outcome: PushHistoryOutcome | OpenedPageHistoryOutcome,
    entriesAdded = 0): void {
    const reportName = timing === PushHistoryWriteTiming.Deferred
      ? 'nav: push history deferred past browser navigation'
      : 'nav: push history caught up or fell back';
    const context = {
      page: openedPage ? getPageName(openedPage.component.componentType) : null,
      outcome: outcome,
      entriesAdded,
      stackDepth: pages?.length ?? 0,
      // where the opened page sits; below the top means a later push landed on it first
      stackPosition: openedPage && pages ? pages.indexOf(openedPage) : -1,
    };

    if (outcome === OpenedPageHistoryOutcome.RefusedDuringBrowserNavigation) {
      this.logging?.trackSilentProblem(reportName, context);
      return;
    }

    this.logging?.trackSilentInfo(reportName, context);
  }

  /**
   * Call a callable page and wait for its result.
   * Opens a page that will return a value via returnAsync().
   * Callable pages are "ephemeral" - they're dropped on browser refresh.
   *
   * Works from ANY component (not just pages) since it returns a Promise.
   *
   * @param pageRef - A CallablePageRef constant (e.g., IInputCalendarPage)
   * @param params - Navigation parameters (type-checked against the page's param type)
   * @returns Promise that resolves with the page's result, or null if cancelled
   */
  async callAsync<PageParamsType extends ValidNavParams<PageParamsType>, ResultType>(
    pageRef: CallablePageRef<PageParamsType, ResultType>,
    params?: PageParamsType
  ): Promise<ResultType | null> {
    if (this.reloadPending) return null;

    const callId = generateLocalParamId();
    const deferred = DeferredPromise.defer<ResultType | null>();

    this.callRegistry.register(callId, deferred.resolve);

    // Add callId to params for the callable page
    const paramsWithCallId = { ...params, _callId: callId } as any;

    // Push the page using internal method
    const pageName = getPageName(pageRef);
    const PageClass = getPageClass(pageRef);
    console.log(`Nav: Call ${pageName}`);

    const newPage = await this.resolvePageFilterAsync(pageName, paramsWithCallId);
    // if filter returns null, app is reloading — bail
    if (newPage == null) {
      console.log(`Nav: Call ${pageName} aborted — app is reloading`);
      this.callRegistry.abandon(callId);
      return null;
    }
    if (newPage !== pageName) {
      console.log(`Page filter redirected from ${pageName} to ${newPage} during call`);
      // if redirected, remove the pending call and return null
      this.callRegistry.abandon(callId);
      await this.internalSetRootAsync(newPage);
      return null;
    }

    // Process _local parameters - store in memory under callId for ephemeral callable pages
    const pageId = generateLocalParamId();
    const { urlParams } = processParamsForNavigation(paramsWithCallId, pageId, callId);

    // Use PageClass directly
    const openedPage = await this.pushCountingOpeningAsync(PageClass, urlParams);

    // Emit navigation change event
    this.emitNavigationChange();

    // Update browser history — the same write as pushAsync, for the page this call opened.
    // See writeBrowserHistoryForPushedPage, which checks browserHistory itself because the
    // deferred write runs long after this call returns.
    this.writeBrowserHistoryForPushedPage(openedPage);

    return deferred;
  }

  /**
   * Write down the result a callable page is returning. Called by
   * CsCallablePage.returnAsync(), which then pops.
   *
   * The result is NOT handed to the caller here. Doing that used to be the whole
   * of the return path, and it delivered the answer too early: the caller page is
   * still marked as having left at that moment (`viewDestroyed`, set when the
   * callable page was pushed), and that flag is only cleared about one frame into
   * the pop. A caller that does even one frame of work before checking the flag
   * therefore threw the answer away — which is how a day picked in the task
   * calendar silently did nothing on the Training and Nutrition pages (#4411).
   * Delivery now happens in `deliverCallResultOnPageDestroy`.
   *
   * A missing entry is normal, not an error. Any path that removes the entry
   * before this runs produces one: `callAsync` drops it when the page filter
   * redirects or the app is reloading, and `deliverCallResultOnPageDestroy`
   * drops it when the page was already destroyed — a stack rebuild such as
   * `setRoot` or `popToRoot` closes a callable page without it ever returning.
   * In every case the caller has already been answered or has gone away, so
   * there is nothing left to record against.
   * @internal
   */
  recordCallResult(callId: string, result: any): void {
    this.callRegistry.recordResult(callId, result);
  }

  /**
   * Hand a callable page's answer to the caller. Called from cs-nav when the
   * page's component is destroyed.
   *
   * Destroy is the right moment for two reasons. It happens only after the pop
   * transition finished, so the caller page has re-entered and is live again —
   * which is the fix for #4411. And it happens on EVERY way a callable page can
   * go away, not just a clean pop: browser back, popTo, popToRoot, setRoot,
   * replaceTop. That second property closes an older gap where those other paths
   * left the caller's promise pending for the rest of the session.
   *
   * What it does NOT promise is that a caller is always answered. A page that is
   * never destroyed never answers: a pop refused by `checkCanLeave`, or one whose
   * popstate never arrives so `navigateBackAsync` gives up on its five-second
   * timeout, leaves the page on screen with its caller still waiting. Answering
   * in that state would be wrong, not helpful — the caller is only safe to resume
   * once the page it opened is gone, which is the whole point of #4411.
   *
   * A page that goes away without recording a result was dismissed, so the caller
   * gets null — the same answer a cancelled call has always produced.
   * @internal
   */
  deliverCallResultOnPageDestroy(callId: string): void {
    // takeAnswer removes the entry as it hands the answer over, so nothing can deliver
    // this call twice — including the deferred delivery below, which runs much later.
    const answer = this.callRegistry.takeAnswer(callId);
    if (!answer) return;

    ephemeralLocalParams.delete(callId);

    const deliver = () => answer.resolve(answer.result);

    // when the pop came from the browser, the popstate handler is still running.
    // A continuation that pushes a page from in there loses its history entry
    // (pushState is suppressed) and can be undone by the pop loop, so wait for
    // the handler to finish — the same reason the returnCallback path defers.
    if (shouldDeferCallDelivery({ popCameFromBrowser: this.browserHistory?.isFromBrowser() ?? false })) {
      this.queuePostBrowserNavCallback(deliver);
    } else {
      deliver();
    }
  }

  /**
   * Atomically replace the current (top) page with a new page.
   * This avoids race conditions from separate popAsync + pushAsync operations.
   * Uses replaceState for browser history, so back button goes to the page before the replaced one.
   *
   * @param pageRef - A PageRef constant (e.g., IHomePage)
   * @param params - Navigation parameters (type-checked against the page's param type)
   */
  async replaceTopAsync<PageParamsType>(pageRef: PageRef<PageParamsType, any>, params?: PageParamsType): Promise<any> {
    const pageName = getPageName(pageRef);
    const PageClass = getPageClass(pageRef);
    console.log(`Nav: ReplaceTop with ${pageName}`);

    // Process _local parameters - store in sessionStorage, keep IDs in URL params
    const pageId = generateLocalParamId();
    const { urlParams } = processParamsForNavigation(params, pageId);

    // Atomically replace the top page
    await this.nav.replaceLast(PageClass, urlParams);

    // Emit navigation change event
    this.emitNavigationChange();

    // Update browser history with replaceState (not pushState)
    if (this.browserHistory) {
      const { url: newUrl } = this.pagesAndUrlForLiveNavStack();
      const pageStack = this.nav.pages.map(p => getPageName(p.component.componentType));
      const breadcrumb = this.getCurrentTrail();

      // Use replaceState to update current history entry (no new entry)
      this.browserHistory.replaceState(newUrl, pageStack, breadcrumb);
    }
  }

  async getPageParamsSafe<PageParamsType>(page: CsPage<PageParamsType>): Promise<PageParamsType> {
    let params = page.navParams.data as PageParamsType;
    if (page.isParamsValid(params)) {
      return params;
    } else {
      console.log(`Page params for ${location.pathname} are unsafe, redirecting to default page.`);
      // Use internal method with undefined to trigger pageFilter redirect to default page
      await this.internalSetRootAsync(undefined);
      throw Error('Redirecting due to unsafe page params');
    }
  }

  /**
   * Pop the current page from the navigation stack.
   * @param result - Optional value to pass back to parent page via returnCallback
   */
  async popAsync<T = void>(result?: T): Promise<any> {
    return this.popAsync_internal({}, result);
  }

  /**
   * Internal pop implementation with NavOptions support.
   * Use popAsync() for normal usage. This is only for internal navigation handling.
   */
  private async popAsync_internal<T = void>(opts: NavOptions, result?: T): Promise<any> {
    // Get current page BEFORE popping (this is the child page)
    const childPage = this.nav.getActive();
    const childParams = childPage?.data || {};

    // Extract callback info from child's params
    const returnCallback = childParams.returnCallback;
    // The EARLY handler, when the parent declared one. Delivered before the closing
    // transition rather than after it — see `returnCallbackImmediate` on CsPage for the two
    // timings and the rule that an early handler must not navigate.
    const returnCallbackImmediate = childParams.returnCallbackImmediate;
    const returnContext = this.getReturnContext(childParams);

    // a callable page's caller is NOT answered here, in either direction. Both a
    // real result and a cancellation are delivered when the page's component is
    // destroyed — see deliverCallResultOnPageDestroy. Answering a dismissal here
    // would put the null back in the same too-early window the result used to be
    // delivered in (#4411), and answering it here at all would mean the paths
    // that destroy a callable page WITHOUT popping still answered nobody.

    let popResult: any;

    // if browser-initiated, pop and invoke callback
    if (opts?.fromBrowser) {
      // Retrieve any pending result from app-initiated pop, with safety check
      let actualResult = result;
      if (this.pendingPopResult !== undefined) {
        // Verify the callback name matches to prevent mismatched results
        // the stored name may be either of the two the parent can declare
        if (this.pendingPopResult.expectedCallback === returnCallback
          || this.pendingPopResult.expectedCallback === returnCallbackImmediate) {
          actualResult = this.pendingPopResult.result;
        } else {
          // Callback mismatch - don't pass the wrong result, log error
          console.error(
            `NavController: pendingPopResult callback mismatch. ` +
            `Expected '${this.pendingPopResult.expectedCallback}', got ` +
            `'${returnCallback}' / '${returnCallbackImmediate}'. ` +
            `Result discarded to prevent passing to wrong callback.`
          );
          // the console line above is dev-console only and reaches nobody on a real device.
          // Refusing to deliver is right — the wrong owner would be worse — but the user's
          // answer is gone and the parent is about to be handed `undefined`, so it must be
          // reported the way the other two dropped-result cases in this file are.
          this.logging?.trackSilentProblem('NavPendingPopResultCallbackMismatch', {
            expectedCallback: this.pendingPopResult.expectedCallback,
            returnCallback,
            returnCallbackImmediate,
            page: childPage ? getPageName(childPage.component.componentType) : null,
            pageCount: this.nav.pages.length,
          });
        }
        this.pendingPopResult = undefined;  // Clear it
      }

      // pop first so the pop loop in handleBrowserNavigation sees the correct depth.
      // The hook does NOT deliver the ordinary handler — that used to happen from in here, and
      // it is the too-early moment deliverReturnCallbackAfterPop exists to remove. It does
      // deliver the parent's EARLY handler when one was declared, which is a different thing
      // and is opted into per parent; see `returnCallbackImmediate` on CsPage. A page with no
      // explicit result may still have supplied one from ionViewCanLeave as `{ result: … }`,
      // and lastLeaveResult only reliably holds it in this window: the rest of the pop awaits
      // transitions, during which a nested navigation's own checkCanLeave would overwrite it.
      let earlyHandlerRan = false;
      popResult = await this.nav.pop(() => {
        if (actualResult === undefined && this.nav.lastLeaveResult !== undefined) {
          actualResult = this.nav.lastLeaveResult as T;
          this.nav.lastLeaveResult = undefined;
        }
        // The EARLY handler. This hook is the moment for it: the leave guard has agreed and the
        // transition has not started, so the parent can react while the page is still sliding
        // away. It is NOT a guarantee that the page leaves — `setPageActive` can still refuse
        // when the ENTERING page's own `canEnter` refuses, and `cs-nav` then puts this page
        // back. That case is reported below rather than left silent. It does NOT go through
        // `deliverReturnCallbackAfterPop` — that helper reads "page still in the stack" as a
        // refused pop, and in here the page is still in the stack because it has not been
        // removed YET. Its refused-pop reporting still covers the ordinary handler below.
        earlyHandlerRan = this.invokeReturnCallbackOwnerIfDeclared(
          returnCallbackImmediate, returnContext, actualResult);
      });

      this.reportEarlyHandlerRanForRefusedPop(earlyHandlerRan, childPage, returnCallbackImmediate);

      // defer until after the popstate handler completes — running the callback now would
      // race: (1) BrowserHistory.pushState skips while a popstate is being handled, so a
      // pushAsync from the callback would not create a history entry, and (2) the pop loop
      // may see the pushed page and undo it. Deferring ensures BrowserHistory is Idle and
      // the pop loop has already exited. The condition is `opts.fromBrowser` and NOT
      // `isFromBrowser()`: recoverStuckBackNavigationAsync pops with fromBrowser set while
      // BrowserHistory is in RecoveringStuckBack, and it is the one that flushes the queue afterwards.
      this.deliverReturnCallbackAfterPop(
        childPage, returnCallback, returnContext, actualResult, ReturnCallbackTiming.AfterBrowserNav);
    } else if (this.browserHistory && !this.browserHistory.isFromBrowser()) {
      // if a page is still sliding in, wait for it first: it has no history entry yet, so a
      // history.back() now could do nothing at all (df-3001b00e3cea)
      if (!(await this.waitForOpeningPushesBeforeAppBackAsync(childPage))) {
        return;
      }
      // For app-initiated pop, store the result and expected callback name
      // The popstate handler will verify and invoke the callback with this result
      // EITHER declared name keeps the result alive. Gating this on `returnCallback` alone
      // silently threw the result away for a page whose parent declared only the early
      // handler, which then ran with `undefined` and did nothing.
      const nameOwningResult = returnCallback || returnCallbackImmediate;
      this.pendingPopResult = nameOwningResult
        ? { result, expectedCallback: nameOwningResult }
        : undefined;
      await this.browserHistory.navigateBackAsync();
      // Don't pop here - the popstate handler will call popAsync_internal with fromBrowser: true
    } else if (this.browserHistory?.isFromBrowser()) {
      // A browser-initiated pop is already in progress (popstate handler running).
      // This second popAsync (e.g., double-click on Save while pop animation runs)
      // must be ignored — the first pop will complete via the popstate handler.
      return;
    } else {
      // Fallback if no unified nav (no browserHistory)
      let actualResult = result;
      let earlyHandlerRan = false;
      popResult = await this.nav.pop(() => {
        // check if ionViewCanLeave returned a result via { result: ... }
        if (this.nav.lastLeaveResult !== undefined) {
          actualResult = this.nav.lastLeaveResult;
          this.nav.lastLeaveResult = undefined;
        }
        // the early handler runs on this path too. A parent that declared one and never saw it
        // fire here could not tell that from a handler that ran and did nothing.
        earlyHandlerRan = this.invokeReturnCallbackOwnerIfDeclared(
          returnCallbackImmediate, returnContext, actualResult);
      });

      this.reportEarlyHandlerRanForRefusedPop(earlyHandlerRan, childPage, returnCallbackImmediate);

      // Emit navigation change event
      this.emitNavigationChange();

      // Invoke callback on parent. Nothing to defer here: there is no browser history, so
      // there is no popstate handler to wait for.
      // NOTE: actualResult can be undefined if the page was closed via header back button
      // instead of explicit save/cancel. Callback implementations MUST handle undefined result.
      this.deliverReturnCallbackAfterPop(
        childPage, returnCallback, returnContext, actualResult, ReturnCallbackTiming.Immediate);
    }

    return popResult;
  }

  /**
   * Hand a popped page's result to the page that owns its `returnCallback`.
   *
   * This is the ONE place `popAsync_internal` delivers from, and the moment it delivers is
   * "after the page has actually gone" — the same moment `deliverCallResultOnPageDestroy`
   * uses for a callable page. Why that moment and not an earlier one, and why the two
   * mechanisms still differ on WHICH ways of closing deliver, are in
   * `design-docs/navigation.md`, "One rule for when a returnCallback is invoked".
   *
   * `poppedPage` is the page that was on top before the pop. `cs-nav.pop` returns void and
   * quietly pushes the page back when `checkCanLeave` or `setPageActive` refuses, so whether
   * it is still in the stack is the only honest answer to "did it go?". A page still on screen
   * answers nobody, matching `deliverCallResultOnPageDestroy`.
   *
   * The callback is fire-and-forget, as it has always been: nothing awaits it, so a callback
   * that rejects surfaces as an unhandled rejection rather than failing the pop.
   */
  private deliverReturnCallbackAfterPop(
    poppedPage: Page | undefined,
    returnCallback: unknown,
    returnContext: unknown,
    result: unknown,
    timing: ReturnCallbackTiming,
  ): void {
    if (!returnCallback || typeof returnCallback !== 'string') return;

    // the page is still in the stack, so the pop was refused and it never left the screen.
    // A MISSING navigation component is not that case and must not be read as one: the pop
    // above ran across an await, and signing out destroys the component while it is
    // suspended (CsNavComponent.ngOnDestroy calls setNav(undefined)), which takes the popped
    // page away along with every other one. Reading `.pages` here threw the same TypeError
    // df-e8962c713884 was about; falling through to the delivery below is right, and
    // `invokeReturnCallbackOwner` is where the teardown itself is dealt with.
    if (!poppedPage || (this.nav && this.nav.pages.includes(poppedPage))) {
      // a refused pop that was carrying a result loses it here: pendingPopResult was
      // consumed and cleared before the pop, and closing the page again travels the
      // no-result route, so the owner would be handed undefined instead. Nothing else
      // notices, so report it rather than dropping the user's answer in silence.
      // The page count is read optionally: a missing `poppedPage` gets in here whatever
      // `this.nav` is, so a sign-out during the pop leaves no stack to count (df-cc7ac91c1951).
      if (result !== undefined) {
        this.logging?.trackSilentProblem('NavReturnResultDroppedOnRefusedPop', {
          returnCallback,
          pageCount: this.nav?.pages.length,
        });
      }
      return;
    }

    if (timing === ReturnCallbackTiming.AfterBrowserNav) {
      // the owner is resolved at flush time, not now: a callback flushed before this one is
      // allowed to push or pop, so the stack can have moved. The arrow is async on purpose —
      // it keeps a missing owner surfacing as a rejected promise, rather than as a throw that
      // `flushPostBrowserNavCallbacks` catches and files under its generic
      // `NavDeferredCallbackThrew` report.
      //
      // The owner as it is NOW is still recorded, so the flush can tell two cases apart: a name
      // no page carried even when the result was queued (a wiring mistake, which throws), and
      // an owner that existed and was closed before the flush — one browser Back that pops the
      // owner in the same loop as this page (#6390). See invokeReturnCallbackOwner.
      const ownerWhenQueued = this.findCallbackTarget(returnCallback);
      this.queuePostBrowserNavCallback(
        async () => this.invokeReturnCallbackOwner(returnCallback, returnContext, result, ownerWhenQueued)
      );
      return;
    }

    this.invokeReturnCallbackOwner(returnCallback, returnContext, result);
  }

  /**
   * Find the page in the stack that owns `returnCallback` and call it with
   * `(returnContext, result)`. Throws when nothing owns it: a result with nowhere to go means
   * the caller is wired up wrongly, and swallowing it would lose the user's answer in silence.
   *
   * That throw covers ONE case and keeps covering it: there is a nav stack, and no page in it
   * carries a method of this name. Two separate cases are handled before it, and neither is a
   * wiring mistake: there is no navigation component at all, and the owner recorded when a
   * browser-route delivery was queued (`ownerWhenQueued`) has been closed since.
   */
  private invokeReturnCallbackOwner(
    returnCallback: string,
    returnContext: unknown,
    result: unknown,
    ownerWhenQueued?: Page,
  ): void {
    // the whole navigation component can be gone by the time this runs. Every delivery route
    // into here starts after an await inside popAsync_internal, and the browser route runs
    // later still, from flushPostBrowserNavCallbacks in BrowserHistory.onPopState's finally.
    // Signing out destroys the component in that window, taking the owner page with every
    // other page, so there is genuinely nobody to hand the result to and nothing is wired
    // wrongly. Reading the stack here threw a TypeError (df-d35ba5ca8f2f), and throwing the
    // "not found in nav stack" error instead would be just as wrong: it would report a wiring
    // bug on every sign-out and bury the real ones it exists to catch.
    //
    // Reported at the INFO tier, not PROBLEM, by the rule reportPushHistoryWrite
    // already applies in this file to the same shape of question: an outcome that is settled
    // is info, and only one leaving a real unrepaired defect is a problem. This one is
    // settled — the stack is about to be rebuilt from root and nobody is left waiting.
    //
    // Note how narrow this is. It is NOT the "no owner, do nothing" rule that
    // design-docs/navigation.md rejects for the pop path: after a setRoot the owner page is
    // destroyed but `this.nav` still holds the new root, so that case still throws.
    if (!this.nav) {
      this.logging?.trackSilentInfo('NavReturnResultDroppedAfterNavTeardown', {
        returnCallback,
      });
      return;
    }

    const targetPage = this.findCallbackTarget(returnCallback);

    // the owner existed when this delivery was queued and has been closed since. One browser
    // Back can land on an entry several pages down, and handleBrowserNavigation's pop loop then
    // closes this page AND its owner before the queue is flushed (#6390). Nobody is left to
    // hand the result to and nothing is wired wrongly, so throwing the "not found" error would
    // report a wiring bug that is not there. Like the teardown drop above, this is NOT the
    // general "no owner, do nothing" rule design-docs/navigation.md rejects: a name that no
    // page carried even at queue time still reaches the throw below.
    //
    // Tier by what was lost: an `undefined` result (a cancel, a back press) loses nothing and
    // is settled, so info; a real result is a user's answer that went nowhere, so a problem.
    if (!targetPage && ownerWhenQueued && !this.nav.pages.includes(ownerWhenQueued)) {
      const context = {
        returnCallback,
        owner: getPageName(ownerWhenQueued.component.componentType),
        pageCount: this.nav.pages.length,
      };
      if (result === undefined) {
        this.logging?.trackSilentInfo('NavReturnResultDroppedOwnerClosed', context);
      } else {
        this.logging?.trackSilentProblem('NavReturnResultDroppedOwnerClosed', context);
      }
      return;
    }

    if (!targetPage) {
      throw new Error(
        `NavController: returnCallback '${returnCallback}' not found in nav stack`
      );
    }
    void (targetPage.instance as any)[returnCallback].call(
      targetPage.instance, returnContext, result
    );
  }

  /**
   * Report an early handler that ran for a pop which was then REFUSED.
   *
   * The leave guard agreeing is not the last word: `setPageActive` still refuses when the
   * ENTERING page's own `canEnter` refuses, and `cs-nav` puts the child back. The parent has
   * then taken the answer with the child still on screen, and pressing the button again hands
   * it a second one — the same food added twice. Rare, since it needs the parent's `canEnter`
   * to refuse (a failing database read, say), but silent data duplication is exactly what must
   * not be silent, and nothing else notices: `deliverReturnCallbackAfterPop`'s own refused-pop
   * report returns before its check for a parent that declared no ordinary handler.
   */
  private reportEarlyHandlerRanForRefusedPop(
    earlyHandlerRan: boolean,
    poppedPage: Page | undefined,
    returnCallbackImmediate: unknown,
  ): void {
    // if the page is still in the stack, the pop was refused after the handler had already run.
    // No navigation component at all is not that case: the pop above ran across an await and a
    // sign-out destroys the component while it is suspended, so there is no stack for the page
    // to still be in. Reading `.pages` there threw a TypeError (df-d35ba5ca8f2f); returning is
    // what this helper already does for a page that has gone.
    if (!earlyHandlerRan || !poppedPage || !this.nav || !this.nav.pages.includes(poppedPage)) return;
    this.logging?.trackSilentProblem('NavEarlyReturnCallbackRanForRefusedPop', {
      returnCallbackImmediate,
      pageCount: this.nav.pages.length,
    });
  }

  /**
   * Call the parent's EARLY handler, when it declared one. Returns whether a name was declared
   * and the call attempted — which the caller needs, because a pop refused after the fact has
   * to be reported (see `reportEarlyHandlerRanForRefusedPop`). It returns true even when the
   * handler threw: the parent was reached, and that is what "attempted" means here.
   *
   * The owner is found by `invokeReturnCallbackOwner`, the same rule the ordinary handler uses.
   * What differs is the FAILURE: on the ordinary path a missing owner throws and travels up,
   * while here it must not, because this runs from `cs-nav.pop`'s hook BEFORE the page leaves
   * the stack and a throw would abandon the pop with its history entry already spent. So it is
   * caught and filed under its own name instead.
   *
   * Like that helper, an async handler's promise is not awaited: the handler is expected to do
   * its visible work up front, and holding the closing transition open until it settles would
   * trade one delay for another.
   */
  private invokeReturnCallbackOwnerIfDeclared(
    returnCallback: unknown,
    returnContext: unknown,
    result: unknown,
  ): boolean {
    if (!returnCallback || typeof returnCallback !== 'string') return false;
    // Nothing this handler does may abort the pop. It is called from `cs-nav.pop`'s hook,
    // which runs BEFORE the page is taken off the stack, so a throw escaping here would leave
    // the page on screen with its history entry already spent — the dead-back shape of #4707.
    // Every other delivery site is insulated from that by running after the page has gone.
    // `invokeReturnCallbackOwner` throws by design when nothing owns the name, and a
    // synchronous handler can throw of its own accord, so both are caught and reported here
    // rather than travelling up.
    try {
      this.invokeReturnCallbackOwner(returnCallback, returnContext, result);
    } catch (e) {
      this.logging?.trackSilentProblem('NavEarlyReturnCallbackThrew', {
        returnCallback,
        error: describeErrorForLog(e),
      });
    }
    return true;
  }

  /**
   * Get return context from child page params.
   * Supports both regular returnContext and returnContext_local (via _local pattern).
   */
  private getReturnContext(childParams: any): any {
    // Use NavParams to get returnContext from local params
    const tempNavParams = new NavParams(childParams);
    return tempNavParams.data.local?.returnContext;
  }

  /**
   * Hand a result to the page that owns `returnCallback`, WITHOUT closing this page (#4967).
   *
   * The ordinary way a page answers its caller is to close itself: `popAsync(result)` pops
   * and then calls the callback. That is right when the page's job is finished. It is wrong
   * when the page's job is to be picked from repeatedly — the food list, where choosing a
   * food should open the portion screen ON TOP of the list, so that logging the food comes
   * straight back to the list and the next food is one tap away. Popping first destroys the
   * list before the portion screen even opens, which is the bug this exists to fix.
   *
   * Safe because it moves nothing: no page is pushed or popped, the browser history is not
   * touched, and `pendingPopResult` is not involved. All it does is find the target and call
   * it. The callback is resolved the same way the pop path resolves it — by walking the whole
   * stack for a page carrying a method of that name — so the owner may sit any number of
   * pages below, which is exactly the case here (the meal page, with the food list between).
   *
   * Throws when nothing in the stack owns the callback, matching the pop path: a result with
   * nowhere to go means the caller is wired up wrongly, and swallowing it would lose the
   * user's chosen food in silence.
   *
   * It deliberately does NOT go through `deliverReturnCallbackAfterPop`, which is the pop
   * path's single delivery point, and the two differences are both load-bearing. That one
   * delivers only once the answering page has gone, which can never be true here — the whole
   * point is that the page stays. And it is fire-and-forget, while this one resolves the owner
   * eagerly and awaits the callback, because `FoodsPage` needs both answers straight away
   * (#4967). Sharing the helper would quietly take both away.
   */
  async deliverResultWithoutPoppingAsync(
    returnCallback: string,
    returnContext: unknown,
    result: unknown,
  ): Promise<void> {
    const targetPage = this.findCallbackTarget(returnCallback);
    if (!targetPage) {
      throw new Error(
        `NavController: returnCallback '${returnCallback}' not found in nav stack`
      );
    }

    const invoke = () => (targetPage.instance as any)[returnCallback].call(
      targetPage.instance, returnContext, result
    );

    // If we got here while the browser's own back/forward handler is still running, wait for
    // it to finish. A callback that pushes a page from inside that handler loses its history
    // entry and can be undone by the pop loop — the same reason the pop path defers
    // (see `deliverCallResultOnPageDestroy`). Nothing reaches this method from a popstate
    // today; the guard is here so that a future caller cannot be bitten by it.
    if (this.browserHistory?.isFromBrowser()) {
      this.queuePostBrowserNavCallback(() => void invoke());
      return;
    }

    // AWAIT the callback on the ordinary path. The caller stays on screen and keeps taking
    // taps while this runs, so it needs to know when the callback has finished deciding — a
    // callback that opens a page has opened it by the time this resolves, and one that gave
    // up has given up. `FoodsPage` uses exactly that to tell a pick that went nowhere from one
    // that worked, and to start accepting taps again in the first case (#4967).
    //
    // This is only as good as the chain beneath it: a callback owner that drops the promise it
    // delegates (`void someChild.handler(...)`) resolves this one microtask later with the real
    // work still running, and the caller then concludes nothing happened. See
    // `design-docs/navigation.md`, "Answering without closing".
    await invoke();
  }

  // find the nearest page in the nav stack that has the given callback method
  // (normally the immediate parent, but a spurious page may have been pushed
  // between parent and child during the transition — see #2762)
  private findCallbackTarget(callbackName: string): Page | undefined {
    // no navigation component means no stack, which means no page owns anything. Callers reach
    // this after an await that a sign-out can outlive, and reading `.pages` there threw a
    // TypeError (df-d35ba5ca8f2f). "Nothing found" is the honest answer, not a crash — what
    // each caller then does about it is its own decision, and invokeReturnCallbackOwner
    // separates this case from a genuine wiring mistake before it ever calls in here.
    if (!this.nav) return undefined;
    for (let i = this.nav.pages.length - 1; i >= 0; i--) {
      const page = this.nav.pages[i];
      if (page.instance && typeof (page.instance as any)[callbackName] === 'function') {
        return page;
      }
    }
    return undefined;
  }

  async popToRootAsync(): Promise<any> {
    const result = this.nav.popToRoot();

    // Emit navigation change event
    this.emitNavigationChange();

    return result;
  }

  async setPagesAsync(pages: { page: string | Type<CsPageComponentInterface<any>>, params: any }[], opts?: NavOptions): Promise<any> {
    let normalizedPages: { page: Type<CsPageComponentInterface<any>>, params: any }[] = [];
    for (let page of pages) {
      normalizedPages.push({
        page: typeof page.page === 'string' ? getAllDeepLinks().find(p => p.name === page.page).component : page.page,
        params: page.params,
      });
    }
    const result = await this.nav.setPages(normalizedPages);

    // Update browser history if this is app-initiated navigation with updateUrl: true
    if (opts?.updateUrl && !opts?.fromBrowser && this.browserHistory && pages.length > 0) {
      const pageNames: string[] = [];
      for (let page of pages) {
        const pageName = typeof page.page === 'string' ? page.page : getPageName(page.page);
        pageNames.push(pageName);
      }

      // Name each page by its page SEGMENT, once, for both the addresses and the
      // back-trail beside them. Read it off normalizedPages, where a page given as a
      // string has already been resolved to its component class — a caller's string is a
      // page NAME, which no reader of a trail accepts (df-5f621deef600).
      const stackSegments = normalizedPages.map(p => ({ segment: getPageSegment(p.page), params: p.params }));

      // Build page stack entries for the back-trail each history entry carries
      const pageStackEntries: PageStackEntry[] = stackSegments.map(p => ({
        pageName: p.segment,
        params: p.params
      }));

      // one address per level of the new stack, each page carrying its own params
      // (df-1ec3f3d10d40 — the old route-segment serializer dropped them all)
      const urls = stackHistoryUrls(stackSegments, this.logging);

      // Build history entries for each page in the new stack
      const historyEntries: { url: string; pageNames: string[]; breadcrumbs: string }[] = [];
      for (let i = 0; i < pages.length; i++) {
        const currentPageNames = pageNames.slice(0, i + 1);
        const currentStackEntries = pageStackEntries.slice(0, i + 1);
        const currentBreadcrumbs = this.breadcrumbService.encodeBreadcrumbs(currentStackEntries);

        historyEntries.push({
          url: urls[i],
          pageNames: currentPageNames,
          breadcrumbs: currentBreadcrumbs
        });
      }

      // Reset browser history and push fresh stack
      await this.browserHistory.resetAndPushStackAsync(historyEntries);
    }

    return result;
  }

  /**
   * Helper function for pages to update their own URL parameters.
   * This preserves the navigation stack while updating only the current page's params.
   *
   * Supports local parameters: values passed in `params.local` are stored in sessionStorage.
   * Access them via `navParams.data.local?.key`.
   *
   * @param params The new parameters to set for the current page
   * @returns Promise that resolves when the URL has been updated
   */
  async updateCurrentPageParamsAsync(params: any): Promise<void> {
    await this.applyCurrentPageParamsAsync(() => params);
  }

  /**
   * Change some of the current page's URL parameters and keep the rest, merging against
   * the same page the update is applied to. Use this rather than reading
   * `getCurrentPageParams()` and handing the result to `updateCurrentPageParamsAsync`: while
   * a newly pushed page is still opening, those two answer for different pages (df-19cb4bb87306).
   *
   * @param patch the parameters to set; every other parameter keeps its value
   */
  async patchCurrentPageParamsAsync(patch: Record<string, any>): Promise<void> {
    await this.applyCurrentPageParamsAsync(current => ({ ...current, ...patch }));
  }

  /**
   * The param update behind `updateCurrentPageParamsAsync` and `patchCurrentPageParamsAsync`.
   *
   * While a pushed page is still opening, "the current page" is the page the browser's current
   * history entry stands for, not the top of the stack. `CsNavComponent.push` adds a page to the
   * stack before its enter transition, and its history entry is written only after it, so for a
   * few seconds the top page has no entry yet and the user is still looking at the page
   * underneath. Updating the top page then put the underneath page's tab on the opening page,
   * and rewriting the current entry with the whole stack made the opening page's own write
   * decide it was already covered, leaving it with no entry and a dead Back (df-19cb4bb87306).
   *
   * Only while a push is opening. At any other time the entry can sit below the top for reasons
   * that do not make the page underneath the one being updated: a page saving its own params as
   * it leaves during a back, or a page whose history write was refused. There the update goes
   * to the top page with the whole stack, as it always did, and so it does when the current
   * entry cannot be trusted. See `design-docs/navigation.md`, "One browser history entry per
   * open page".
   *
   * @param buildParams answers the page's new params from its current ones
   */
  private async applyCurrentPageParamsAsync(buildParams: (current: any) => any): Promise<void> {
    // Wait for nav to be ready
    await this.navReady;

    if (!this.nav) {
      console.error('NavController: Cannot update params - nav not available');
      return;
    }

    const pages = this.nav.pages;
    if (!pages || pages.length === 0) {
      console.error('NavController: Cannot update params - no active page');
      return;
    }

    // while a push is opening, the page the browser's current entry stands for; otherwise, or
    // when that entry cannot be trusted, the top page
    const entries = this.historyEntriesForOpenPages(pages);
    const depth = this.pushesOpening > 0
      ? this.browserHistory?.trustedCurrentEntryDepth(entries) ?? pages.length
      : pages.length;
    const currentPage = pages[depth - 1];

    // Process _local parameters - store in sessionStorage, keep IDs in URL params
    const pageId = generateLocalParamId();
    const { urlParams } = processParamsForNavigation(buildParams(currentPage.data ?? {}), pageId);

    // Update the params on the current page instance without recreating it
    currentPage.data = urlParams;

    // Update navParams on the component instance
    for (const key in currentPage.instance.navParams.data) {
      delete currentPage.instance.navParams.data[key];
    }
    for (const key in urlParams) {
      currentPage.instance.navParams.data[key] = urlParams[key];
    }
    hydrateLocalParams(currentPage.instance.navParams.data);

    // rewrite the current entry with the updated params, describing the pages up to that page
    // and never a page above it whose own entry has not been written yet
    const rewriteDepth = this.historyRewriteDepthForParamUpdate(currentPage, depth, entries);
    if (this.browserHistory && rewriteDepth != null) {
      const entry = this.historyEntriesForOpenPages(pages)[rewriteDepth - 1];
      this.browserHistory.replaceState(entry.url, entry.pageNames, entry.breadcrumbs);
    }
  }

  /**
   * How deep the current entry a param update rewrites should reach, or undefined to leave
   * history alone.
   *
   * Normally the depth of the updated page. But a page whose own history write is still queued
   * behind a browser navigation has no entry yet, and rewriting the current entry to reach it
   * would make that write decide it is already covered and write nothing (df-9b9de115255a). So
   * for such a page the update leaves history to the queued write, which reads these params
   * when it runs. The exception is a current entry that already reaches the page, because
   * something else wrote it: then the queued write will add nothing, so the entry is refreshed
   * where it stands, never extended.
   */
  private historyRewriteDepthForParamUpdate(page: Page, depth: number, entries: HistoryEntry[]): number | undefined {
    if (!this.pagesAwaitingDeferredHistory.has(page)) {
      return depth;
    }
    const currentDepth = this.browserHistory?.trustedCurrentEntryDepth(entries);
    return currentDepth != null && currentDepth >= depth ? currentDepth : undefined;
  }

  /** @deprecated Use trail-based navigation instead. This method will be removed. */
  async patchHistoryAsync(parentPage: string, params: any) {
    void parentPage; // Suppress unused parameter warning
    void params; // Suppress unused parameter warning
    console.warn('patchHistoryAsync is deprecated and will be removed. Use trail-based navigation instead.');
    // Legacy method kept for backward compatibility during migration
    // TODO: Remove this method after all pages are updated
  }

  getPrevious(view?: Page): Page {
    return this.nav.getPrevious(view);
  }

  /**
   * The page on top of the stack.
   *
   * This THROWS when there is no navigation component, and that is deliberate — do not
   * "fix" it with a `?.`. Every caller here reads a field off the result straight away
   * (`.id`, `.instance`, `.component`), so there is no honest answer to give them when
   * there is no stack: answering undefined would only move the same crash one frame
   * deeper, with a less informative message. A read that DOES have an absent answer
   * gives it and guards itself — see `getActivePageName`, `getCurrentPageParams` and
   * `getPageStack` below, and df-f95a3dd7e372 for the window.
   */
  getActive(): Page {
    return this.nav.getActive();
  }

  /**
   * Get the current page's parameters
   * Useful for child components that need access to page params
   *
   * No navigation component means no page, which is the `{}` this already answers for a
   * missing page. When that window happens: design-docs/navigation.md, "Reading the
   * stack when there is no navigation component" (df-f95a3dd7e372).
   */
  getCurrentPageParams(): any {
    if (!this.nav) {
      return {};
    }
    const activePage = this.getActive();
    return activePage ? activePage.data : {};
  }

  /**
   * Get the active page's name from the nav stack (not URL).
   * Use this with getCurrentPageParams() for consistent page state.
   *
   * Answers undefined when there is no navigation component, which is the same answer
   * this already gives for a missing page. Reading it through `getActive()` unguarded
   * threw there. When that window happens and which reads guard themselves are in
   * design-docs/navigation.md, "Reading the stack when there is no navigation
   * component" (df-f95a3dd7e372).
   */
  getActivePageName(): string | undefined {
    if (!this.nav) {
      return undefined;
    }
    const activePage = this.getActive();
    return activePage ? getPageName(activePage.component.componentType) : undefined;
  }

  /**
   * Get all page names in the current navigation stack.
   * Useful for checking if any page in the stack has certain properties (e.g., hides side menu).
   */
  getPageStack(): string[] {
    if (!this.nav || !this.nav.pages || this.nav.pages.length === 0) {
      return [];
    }
    return this.nav.pages.map(p => getPageName(p.component.componentType));
  }

  /**
   * Call a method on the parent page (the page that pushed the current page).
   * This enables child pages to call back to their parent without passing functions.
   *
   * @param methodName Name of the method to call on the parent page
   * @param args Arguments to pass to the method
   * @returns The result of the method call
   */
  async callParentAsync<T>(methodName: string, ...args: any[]): Promise<T> {
    const parentPage = this.getPrevious();
    if (!parentPage?.instance) {
      throw new Error(`NavController: No parent page found`);
    }

    const method = (parentPage.instance as any)[methodName];
    if (typeof method !== 'function') {
      throw new Error(
        `NavController: Method '${methodName}' not found on parent page ` +
        `'${parentPage.component?.componentType?.name}'`
      );
    }

    return method.apply(parentPage.instance, args);
  }

  // Check if we can go back based on the navigation stack
  canGoBack() {
    // Navigation stack is the source of truth
    const canGo = this.nav && this.nav.pages.length > 1;
    return canGo;
  }

  /**
   * Emit navigation stack change event to notify subscribers (like back button)
   */
  private emitNavigationChange() {
    const canGoBack = this.canGoBack();
    const pagesLength = this.nav ? this.nav.pages.length : 0;
    this.pubSub.publish(navigationStackChangedEvent, { canGoBack, pagesLength });
  }

  /**
   * Returns true when params carry a localId whose sessionStorage anchor is gone
   * (OS-evicted tab, manual clear, URL opened in a different tab/process). Used as
   * a preflight on URL-restore to short-circuit deep URLs we cannot rebuild.
   */
  private hasMissingLocalIdAnchor(params: any): boolean {
    if (!params || typeof params !== 'object') return false;
    const localId = params.localId;
    if (typeof localId !== 'string' || localId.length === 0) return false;
    if (sessionStorage.getItem(getStorageKey(localId)) !== null) return false;
    console.log(
      `Nav: URL references localId '${localId}' which is no longer in sessionStorage. ` +
      `Deep URL is unrestorable; routing to home.`
    );
    return true;
  }

  /**
   * Restore navigation stack from trail during page refresh
   */
  private async restoreNavigationStack(pageStack: PageStackEntry[], currentPage: string, currentParams: any): Promise<void> {
    this.isRestoringNavStack = true;
    // L10 (#3986): fresh restore — clear any abandon flag left by a prior watchdog fire.
    this.navRestoreAbandoned = false;

    try {
      // Check if current page is ephemeral (extends CsCallablePage)
      const currentPageClass = getPageClassBySegment(currentPage);
      const currentIsEphemeral = currentPageClass && isCallablePageClass(currentPageClass);

      if (currentIsEphemeral) {
        console.log(`Nav: Current page ${currentPage} is ephemeral, truncating restoration`);
        // if we have a pageStack, go to the last page that isn't ephemeral
        let foundNonEphemeral = false;
        for (let i = pageStack.length - 1; i >= 0; i--) {
          const pageClass = getPageClassBySegment(pageStack[i].pageName);
          if (pageClass && !isCallablePageClass(pageClass)) {
            const safePageName = getPageName(pageClass);
            // Truncate pageStack to non-ephemeral portion
            pageStack = pageStack.slice(0, i + 1);
            currentPage = safePageName;
            currentParams = pageStack[i].params;
            foundNonEphemeral = true;
            break;
          }
        }
        // if all pages are ephemeral (shouldn't happen), just go to root
        if (!foundNonEphemeral) {
          console.log(`Nav: All pages are ephemeral, going to root`);
          await this.internalSetRootAsync('HomePage', {});
          return;
        }
      }

      // Check pageStack for ephemeral pages and truncate
      let truncatedStack: PageStackEntry[] = [];
      for (const page of pageStack) {
        const pageClass = getPageClassBySegment(page.pageName);
        if (pageClass && isCallablePageClass(pageClass)) {
          console.log(`Nav: Truncating stack at ephemeral page ${page.pageName}`);
          break;
        }
        truncatedStack.push(page);
      }
      pageStack = truncatedStack;

      // for each set of params parsed from the URL, check the localId anchor
      for (const page of pageStack) {
        if (this.hasMissingLocalIdAnchor(page.params)) {
          await this.internalSetRootAsync('HomePage', {});
          return;
        }
      }
      if (this.hasMissingLocalIdAnchor(currentParams)) {
        await this.internalSetRootAsync('HomePage', {});
        return;
      }

      if (pageStack.length === 0) {
        // No trail, just set current page as root
        await this.internalSetRootAsync(currentPage, currentParams);
        return;
      }

      // Start with the first page in the trail as root
      const rootPage = pageStack[0];
      const rootPageComponent = getPageClassBySegment(rootPage.pageName);

      if (!rootPageComponent) {
        console.warn(`Nav: Root page ${rootPage.pageName} not found, falling back to current page`);
        await this.internalSetRootAsync(currentPage, currentParams);
        return;
      }

      const rootPageComponentName = getPageName(rootPageComponent);

      if (!rootPageComponentName) {
        console.error(`Nav: Could not get page name for component, falling back to current page`);
        await this.internalSetRootAsync(currentPage, currentParams);
        return;
      }

      try {
        await this.internalSetRootAsync(rootPageComponentName, rootPage.params);
      } catch (error) {
        // the person lands on the current page with no back trail
        this.logging?.trackSilentProblem('NavRestoreRootFailed', {
          page: rootPageComponentName,
          fallbackPage: currentPage,
          error: describeError(error),
        });
        await this.internalSetRootAsync(currentPage, currentParams);
        return;
      }

      // Push each subsequent page in the trail
      for (let i = 1; i < pageStack.length; i++) {
        // L10 (#3986): stop pushing further pages once the restore has been abandoned (a
        // merely-slow restore asked to stop, or the Step 12 watchdog declaring it dead). Checked
        // between pushes — an already-in-flight nav.push cannot be interrupted here.
        if (!shouldContinueNavRestore({ abandoned: this.navRestoreAbandoned })) {
          console.log('Nav: nav restore abandoned, stopping page-load loop');
          return;
        }
        const page = pageStack[i];
        const pageComponent = getPageClassBySegment(page.pageName);

        if (!pageComponent) {
          console.warn(`Nav: Page ${page.pageName} not found in trail, skipping`);
          continue;
        }

        console.log(`Nav: Pushing page from breadcrumbs: ${page.pageName}`);
        const stackLengthBefore = this.nav.pages.length;
        await this.nav.push(pageComponent, page.params);

        // Check if navigation was redirected (stack length decreased or became 1)
        const stackLengthAfter = this.nav.pages.length;
        if (stackLengthAfter <= 1 || stackLengthAfter < stackLengthBefore) {
          console.log(`Nav: Page ${page.pageName} guard redirected navigation, aborting restoration`);
          return; // Abort the rest of the restoration
        }
      }

      // Finally, push the current page if it's not already in the stack
      const lastPageInStack = pageStack[pageStack.length - 1];
      const lastPageComponent = getPageClassBySegment(lastPageInStack.pageName);
      const lastPageComponentName = lastPageComponent ? getPageName(lastPageComponent) : null;
      if (lastPageComponentName !== currentPage) {
        const currentPageComponent = getAllDeepLinks().find(p => p.name === currentPage);

        if (currentPageComponent) {
          console.log(`Nav: Pushing current page: ${currentPage}`);
          try {
            await this.nav.push(currentPageComponent.component, currentParams);
          } catch (error) {
            // the person is left on the last page of the trail, not the page in the address. this
            // used to be a console line guessing at a guard redirect; the report is how we find out
            this.logging?.trackSilentProblem('NavRestoreCurrentPagePushFailed', {
              page: currentPage,
              error: describeError(error),
            });
          }
        } else {
          console.warn(`Nav: Could not find component for current page: ${currentPage}`);
        }
      }
      // Sync URL with navigation stack to ensure getCurrentPage() returns the correct page
      this.syncUrlWithNavigationStack();

      // rebuild browser history entries for each page in the stack so back navigation works
      // (without this, there's only one history entry and history.back() has nothing to pop to)
      this.buildBrowserHistoryForStack();
    } finally {
      this.isRestoringNavStack = false;
      // L10 (#3986): a re-route requested during the restore was deferred so it couldn't clobber
      // the stack mid-rebuild — run it now that the restore is done.
      const pending = this.pendingRerouteAfterRestore;
      this.pendingRerouteAfterRestore = null;
      if (pending) {
        console.log('Nav: running deferred re-route after nav restore completed');
        // the deferred re-route is fire-and-forget — surface a rejection instead of losing it
        // as an unhandled rejection with no telemetry (the L10 cohort would silently stay on
        // the wrong page).
        void pending().catch(err => {
          this.logging.trackAbnormalError(`Nav: deferred re-route after restore failed: ${(err as { message?: string })?.message ?? err}`);
        });
      }
    }
  }

  // L8 (#3986): the navRestoring watchdog's sanctioned guard-breaker. A restore wedged inside an
  // in-flight `await nav.push` never reaches the finally that clears isRestoringNavStack, and the
  // between-pushes abandon flag can't interrupt that await — so the watchdog must FORCE the guard
  // open before its recovery setRootAsync, otherwise the re-route would defer forever behind a
  // restore that never completes. Also drops any queued deferred re-route (it targeted the dead
  // restore). This is the ONE sanctioned path that breaks the defer-guard; healthy concurrency
  // still goes through rerouteRespectingRestore.
  forceClearNavRestore(): void {
    this.navRestoreAbandoned = true;
    this.isRestoringNavStack = false;
    this.pendingRerouteAfterRestore = null;
  }

  // L10 (#3986): run a re-route now, or defer it until an in-flight nav restore finishes so it
  // can't clobber the half-rebuilt stack. (The Step 12 watchdog does NOT go through here — it
  // force-clears the restore with forceClearNavRestore first, then re-roots directly, so a dead
  // restore never makes a recovery re-route queue forever.)
  async rerouteRespectingRestore(reroute: () => Promise<void>): Promise<void> {
    if (shouldDeferReroute({ isRestoring: this.isRestoringNavStack })) {
      console.log('Nav: deferring re-route until nav restore completes');
      this.pendingRerouteAfterRestore = reroute;
      return;
    }
    await reroute();
  }

  /**
   * After restoreNavigationStack, push browser history entries for each page
   * so that popAsync → history.back() has previous entries to navigate to.
   */
  private buildBrowserHistoryForStack(): void {
    if (!this.browserHistory || !this.nav || this.nav.pages.length <= 1) {
      return;
    }

    // one entry per level of the stack, built the same way a push builds them
    const entries = this.historyEntriesForOpenPages(this.nav.pages);

    // first entry replaces current, remaining entries get pushed
    this.browserHistory.replaceState(entries[0].url, entries[0].pageNames, entries[0].breadcrumbs);
    for (let i = 1; i < entries.length; i++) {
      this.browserHistory.pushState(entries[i].url, entries[i].pageNames, entries[i].breadcrumbs);
    }
    console.log(`NavController: Rebuilt ${entries.length} browser history entries after navigation restoration`);
  }

  async goToRoot() {
    return this.nav.popToRoot();
  }

  length(): number {
    return this.nav.pages.length;
  }

  getByIndex(i: number) {
    return this.nav.pages[i];
  }

  /**
   * How deep the stack should be after a browser back that landed on an entry standing for
   * `landedDepth` pages (#6411).
   *
   * A page gets its history entry only once its opening slide has finished, so a back that
   * arrives during the slide lands one entry lower than the page the user sees. Popping down to
   * the landed entry's depth would close the opening page AND the page under it. So this first
   * waits for every push still opening, which makes the close an ordinary pop of a page that
   * finished opening rather than one racing its own slide. A push that finishes now queues its
   * history write and is marked in pagesAwaitingDeferredHistory, so the pages still without an
   * entry are exactly the marked ones; each of them adds one page to the depth to stop at. The
   * back always closes at least one page. See design-docs/navigation.md, "A browser back during
   * an opening slide".
   */
  private async targetDepthForBrowserBackAsync(landedDepth: number): Promise<number> {
    if (this.openingPushes.size > 0 && !(await this.waitForOpeningPushesAsync())) {
      // a push that has not finished within the wait is itself wrong; close pages as the landed
      // entry says, which is what every back did before #6411
      this.logging?.trackSilentProblem('nav: browser back gave up waiting for an opening page', {
        landedDepth,
        stackDepth: this.nav?.pages.length ?? 0,
        pushesOpening: this.pushesOpening,
      });
      return landedDepth;
    }

    const pages = this.nav?.pages ?? [];
    const pagesWithoutEntry = pages.filter(page => this.pagesAwaitingDeferredHistory.has(page)).length;
    if (pagesWithoutEntry === 0) {
      return landedDepth;
    }
    const targetDepth = Math.max(landedDepth, Math.min(landedDepth + pagesWithoutEntry, pages.length - 1));
    // settled when reported: the pages to close are decided, and the history is repaired after
    // the pop. Counted so we learn how often a back lands during an opening slide.
    this.logging?.trackSilentInfo('nav: browser back during an opening page closes only that page', {
      landedDepth,
      pagesWithoutEntry,
      stackDepth: pages.length,
      targetDepth,
    });
    return targetDepth;
  }

  /**
   * Before an app-initiated back, wait for every page that is still opening
   * (df-3001b00e3cea).
   *
   * A page gets its history entry only once its opening slide has finished, so during the slide
   * the browser still stands on the entry of the page under it. When that is the browser's
   * oldest entry, history.back() does nothing and fires no popstate, and the page stays open
   * until navigateBackAsync's five-second recovery closes it. Waiting first gives the opening
   * page its entry, so the back is an ordinary one. This is the app-initiated half of what
   * targetDepthForBrowserBackAsync does for a browser back (#6411).
   *
   * A page whose history write is deferred past a browser navigation cannot reach here: that
   * queue is flushed in the same synchronous finally that ends the browser navigation, and this
   * branch runs only when none is being handled.
   *
   * @param childPage the page this back was asked to close, read before the wait
   * @returns false when the back must not go ahead
   */
  private async waitForOpeningPushesBeforeAppBackAsync(childPage: Page | undefined): Promise<boolean> {
    if (this.openingPushes.size === 0) {
      return true;
    }
    const waitRanOut = !(await this.waitForOpeningPushesAsync());
    if (waitRanOut) {
      // a push that has not finished within the wait is itself wrong; report it, and go back as
      // before unless the checks below show the back no longer applies
      this.logging?.trackSilentProblem('nav: app back gave up waiting for an opening page', {
        stackDepth: this.nav?.pages.length ?? 0,
        pushesOpening: this.pushesOpening,
      });
    }

    // the checks below run however the wait ended: up to OPENING_PUSH_WAIT_MS has passed
    // since the back was asked for
    if (!this.nav || !this.browserHistory) {
      // torn down during the wait (a sign-out); there is no stack left to go back in
      return false;
    }
    if (this.browserHistory.isFromBrowser()) {
      // a browser back arrived during the wait and is already closing pages; going back as
      // well would close a second page, so drop this one as a back during a browser
      // navigation is always dropped
      return false;
    }
    if (childPage != null && this.nav.getActive() !== childPage) {
      // the page left some other way during the wait, or a page opened during the wait now
      // covers it; history.back() closes whatever is on top, so it would close a page the user
      // did not ask to close
      this.logging?.trackSilentInfo('nav: app back skipped, its page is no longer on top', {
        page: getPageName(childPage.component.componentType),
        stillOpen: this.nav.pages.includes(childPage),
        stackDepth: this.nav.pages.length,
        waitRanOut,
      });
      return false;
    }
    return true;
  }

  /**
   * Wait for every push that is still opening, for at most OPENING_PUSH_WAIT_MS.
   *
   * @returns false when the wait ran out with a push still opening.
   */
  private async waitForOpeningPushesAsync(): Promise<boolean> {
    let timer: TimerHandle | undefined;
    let ranOut = false;
    const deadline = new Promise<void>(resolve => {
      timer = this.browserEnv.setTimer(() => {
        ranOut = true;
        resolve();
      }, OPENING_PUSH_WAIT_MS);
    });
    // while pushes are opening, wait for them; a push started meanwhile (from an opening page's
    // own enter hook, say) joins the set and is waited for on the next round, within one deadline
    while (this.openingPushes.size > 0 && !ranOut) {
      await Promise.race([Promise.allSettled([...this.openingPushes]), deadline]);
      if (!ranOut) {
        // each push's own caller still has to resume and queue its history write, which is what
        // marks the page in pagesAwaitingDeferredHistory. Those are promise callbacks, and a
        // timer runs only once every queued promise callback has run, so after this they all have.
        await new Promise<void>(resolve => this.browserEnv.setTimer(resolve, 0));
      }
    }
    this.browserEnv.clearTimer(timer);
    return !ranOut;
  }

  /**
   * Give the browser back an entry for every open page above the one it landed on, after a back
   * that closed fewer pages than the landed entry's depth says (#6411). See
   * targetDepthForBrowserBackAsync.
   */
  private restoreEntriesAboveLandedEntry(landedDepth: number): void {
    if (!this.nav || this.nav.pages.length === 0) {
      return;
    }
    if (this.nav.pages.length <= landedDepth) {
      // nothing is left above the landed entry; describe the stack as every other back does
      this.syncUrlWithNavigationStack();
      return;
    }
    this.browserHistory.restoreEntriesAboveLandedEntry(this.historyEntriesForOpenPages(this.nav.pages), landedDepth);
  }

  /**
   * Put the browser's entries back after a page refused to be left during a browser back. When
   * pages above the landed entry were meant to stay open (#6411), every one of them needs its
   * entry back, and restoreBlockedNavigation would push a single entry for the whole stack.
   */
  private restoreHistoryAfterRefusedLeave(landedDepth: number, closesFewerThanEntrySays: boolean): void {
    if (closesFewerThanEntrySays) {
      this.restoreEntriesAboveLandedEntry(landedDepth);
    } else {
      this.restoreBlockedNavigation();
    }
  }


  /**
   * Handle browser-initiated navigation (back/forward button).
   * Called by BrowserHistoryService when browser navigation occurs.
   */
  async handleBrowserNavigation(url: string, state: NavigationState | null): Promise<void> {
    // Parse the URL to get the target page
    const targetPage = getPageNameByPath(url);
    if (!targetPage) {
      console.warn(`NavController: Unknown page for URL ${url}`);
      return;
    }

    // Check if we can restore from state
    if (state && state.pageNames && state.pageNames.length > 0) {
      // We have stored page stack, try to restore it
      const landedDepth = state.pageNames.length;
      const currentDepth = this.nav ? this.nav.pages.length : 0;

      if (landedDepth < currentDepth) {
        // going back: usually pop to the landed entry's depth, but if a page is still opening it
        // has no entry yet, so it is closed in place of the page under it (#6411)
        const targetDepth = await this.targetDepthForBrowserBackAsync(landedDepth);
        const closesFewerThanEntrySays = targetDepth > landedDepth;
        while (this.nav && this.nav.pages.length > targetDepth) {
          const depthBefore = this.nav.pages.length;
          await this.popAsync_internal({ fromBrowser: true, updateUrl: false });
          // the nav component can be destroyed while that pop is suspended, and signing out
          // does exactly that: CsNavComponent.ngOnDestroy calls setNav(undefined). There is
          // then no stack left to compare depthBefore against, and reading this.nav.pages
          // here threw a TypeError that escaped as an unhandled rejection (df-e8962c713884).
          // Stop the loop instead — which is what its own `while` condition already says to
          // do — and let the two calls after it run: both syncUrlWithNavigationStack and
          // emitNavigationChange already handle a missing component, so nothing downstream
          // needs a decision about it.
          if (!this.nav) {
            break;
          }
          // if ionViewCanLeave blocked the navigation, restore browser history
          if (this.nav.pages.length === depthBefore) {
            // navigation was blocked, push current state back to restore history
            // (browser already went back, so we need to push forward)
            this.restoreHistoryAfterRefusedLeave(landedDepth, closesFewerThanEntrySays);
            return;
          }
        }

        if (closesFewerThanEntrySays) {
          // the browser stands on an entry below the pages left open, so give them their entries
          // back; rewriting the landed entry to describe the whole stack would leave the browser
          // one entry short of the open pages
          this.restoreEntriesAboveLandedEntry(landedDepth);
        } else {
          // Always sync URL with navigation stack after popping
          // This ensures the URL correctly reflects the current page, even if breadcrumbs are incomplete
          this.syncUrlWithNavigationStack();
        }
      } else if (landedDepth > currentDepth) {
        // Going forward - need to reconstruct pages
        // This is more complex and may need the page params from URL/storage
        await this.reconstructPageStack(state.pageNames, url);
      }
    } else {
      // No state stored, fall back to URL-based navigation
      // This happens after reload or when state is lost
      await this.navigateToUrlPage(targetPage, url);
    }

    // Notify components about navigation stack changes (like back button)
    this.emitNavigationChange();
  }

  /**
   * Close the top page after the browser refused to go back (#4707).
   *
   * Normally a page is removed inside the popstate handler, because that is the only
   * point at which the browser and the app agree on what the stack now looks like.
   * When the browser fires no popstate at all — which it does whenever its current
   * history entry is the oldest one it has — nothing removes the page and the user is
   * stuck on a screen whose close button does nothing. BrowserHistory calls this after
   * it has waited five seconds and established that the back genuinely did not happen.
   *
   * Two things have to be true afterwards or the user is stuck again on the next press:
   * the parent page must still be told what came back, and the browser's history entry
   * must be rewritten to describe the stack as it now is.
   *
   * The caller flushes the deferred return callbacks, NOT this method — the flush in
   * BrowserHistory.navigateBackAsync's finally says why the ordering matters.
   */
  async recoverStuckBackNavigationAsync(): Promise<void> {
    // pop through the shared path, not a bare this.nav.pop(): that path is what runs
    // ionViewCanLeave, hands the result to the parent's returnCallback, and answers a
    // callable page's caller. A bare pop would close the page while silently dropping
    // every one of those.
    try {
      await this.popAsync_internal({ fromBrowser: true, updateUrl: false });
    } finally {
      // Both of these run even when the pop threw, and both have to. popAsync_internal
      // can throw on a real path — cs-nav.pop's own synchronous work raises, starting
      // with the leaving page's _navPrepareLeave and everything setPageActive does to
      // the DOM — and a history entry left describing the page that was just closed
      // leaves back dead in exactly the way this method exists to clear, so the user
      // would still be stuck. (The leave HOOK is not one of those: setPageActive calls
      // ionViewWillLeave with `void`, so a rejection there never reaches this frame.)
      //
      // A missing returnCallback owner is NOT one of those paths any more. This call
      // pops with fromBrowser set, so deliverReturnCallbackAfterPop queues the delivery;
      // the throw then happens at flush time, in this method's own caller, and reaches
      // the browser as an unhandled rejection rather than this try/catch.
      this.syncUrlWithNavigationStack();
      this.emitNavigationChange();
    }
  }

  /**
   * Sync the URL trail parameter with the current navigation stack.
   * This ensures the URL accurately reflects the current page stack after navigation.
   */
  private syncUrlWithNavigationStack(): void {
    if (!this.nav || !this.nav.pages || this.nav.pages.length === 0) {
      return;
    }

    // Convert entire navigation stack to pages, and generate the URL directly from it
    const { pages: allPages, url: newUrl } = this.pagesAndUrlForLiveNavStack();

    // Build fresh state with correct pageNames from the actual nav stack.
    // Using history.state would preserve stale pageNames from a previous navigation
    // sequence, causing handleBrowserNavigation to pop the wrong number of pages.
    const pageNames = this.historyPageNames(this.nav.pages);
    // Go through BrowserHistory so its record of "the address that matches what is on screen"
    // stays truthful — writing the address here directly left that record naming the page a
    // browser back had just popped off. replaceState, not pushState: pushState returns early
    // while a popstate is being handled, which is exactly when the popstate handler calls us.
    // Why it matters and what it cost: nav-url-sync-bookkeeping.test.ts (df-ee9d044b54be).
    const breadcrumbs = (this.browserEnv.historyState() as NavigationState | null)?.breadcrumbs || '';
    this.browserHistory.replaceState(newUrl, pageNames, breadcrumbs);

    console.log('NavController: Synced URL with navigation stack', {
      pageCount: allPages.length,
      pages: allPages.map(p => p.segment),
      newUrl: newUrl
    });
  }

  /**
   * Restore browser history after navigation was blocked by ionViewCanLeave.
   * Browser back already moved history back, so we push a new entry to restore.
   */
  restoreBlockedNavigation(): void {
    if (!this.nav || !this.nav.pages || this.nav.pages.length === 0) {
      return;
    }

    // Convert navigation stack to pages, and the address that represents them
    const { pages: allPages, url: newUrl } = this.pagesAndUrlForLiveNavStack();

    // built from the live nav stack, for the same reason syncUrlWithNavigationStack gives
    // above: this list is what handleBrowserNavigation counts to decide how many pages to
    // pop, so it has to describe the open pages rather than the names a previous navigation
    // recorded.
    const pageNames = this.historyPageNames(this.nav.pages);
    // A fresh trail, where syncUrlWithNavigationStack keeps whatever is on history.state.
    // The trail sitting there describes the entry the browser just popped to — the very
    // move being undone here — so reusing it would restore the address with the trail of
    // the page the user was blocked from leaving for. A trail names pages by their address
    // segment, never their registered name, so it reads p.segment and not pageNames.
    const breadcrumbs = this.breadcrumbService.encodeBreadcrumbs(
      allPages.map(p => ({
        pageName: p.segment,
        params: p.params || {}
      }))
    );

    // Push new state to restore history (browser already went back). Go through
    // BrowserHistory so its record of the on-screen address, the warm-resume address and
    // the stored page stack are all written — writing the entry here directly left all
    // three naming whatever the last ordinary navigation wrote. pushRestoredEntry, not
    // pushState: pushState returns early while a popstate is being handled, and the
    // caller that matters reaches us in exactly that state (df-3d9c1692ab6c).
    this.browserHistory.pushRestoredEntry(newUrl, pageNames, breadcrumbs);

    console.log('NavController: Restored browser history after blocked navigation', {
      pageCount: allPages.length,
      url: newUrl
    });
  }

  /**
   * Reconstruct page stack from stored state.
   * Used when navigating forward or after reload.
   */
  private async reconstructPageStack(pageNames: string[], targetUrl: string): Promise<void> {
    // For now, just navigate to the target page
    // A full implementation would rebuild the entire stack from pageNames
    void pageNames; // TODO: Use this to rebuild full stack
    const targetPage = getPageNameByPath(targetUrl);
    if (targetPage) {
      await this.internalSetRootAsync(targetPage, null, { fromBrowser: true, updateUrl: false });
    }
  }

  /**
   * Navigate to a page based on URL.
   * Used when we don't have stored state.
   */
  private async navigateToUrlPage(pageName: string, url: string): Promise<void> {
    // Parse params and trail from URL
    const segment = this.serializer.parse(url);
    const params = segment?.data;
    const breadcrumbs = segment?.breadcrumbs;

    // Check if page is already current
    if (this.isPageCurrent(pageName)) {
      return;
    }

    // If we have a valid trail, use it to reconstruct the page stack
    if (breadcrumbs && this.breadcrumbService.validateBreadcrumbs(breadcrumbs)) {
      const trailPages = this.breadcrumbService.decodeBreadcrumbs(breadcrumbs);
      const pages: { page: string | Type<CsPageComponentInterface<any>>, params: any }[] = [];

      for (const trailPage of trailPages) {
        const pageClass = getPageClassBySegment(trailPage.pageName);
        if (pageClass) {
          pages.push({
            page: pageClass,
            params: trailPage.params
          });
        }
      }

      if (pages.length > 0) {
        await this.setPagesAsync(pages, { fromBrowser: true, updateUrl: false });
        return;
      }
    }

    // Fallback: try to build trail from defaultHistory
    const fallbackTrail = this.buildFallbackTrail(pageName, params);
    if (fallbackTrail) {
      const trailPages = this.breadcrumbService.decodeBreadcrumbs(fallbackTrail);
      const pages: { page: string | Type<CsPageComponentInterface<any>>, params: any }[] = [];

      for (const trailPage of trailPages) {
        const pageClass = getPageClassBySegment(trailPage.pageName);
        if (pageClass) {
          pages.push({
            page: pageClass,
            params: trailPage.params
          });
        }
      }

      if (pages.length > 0) {
        await this.setPagesAsync(pages, { fromBrowser: true, updateUrl: false });
        return;
      }
    }

    // Final fallback: just navigate to the page as root
    await this.internalSetRootAsync(pageName, params, { fromBrowser: true, updateUrl: false });
  }

}
